import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../services/api';
import { toast } from 'react-toastify';
import { routeStaticMapUrl, buildGoogleMapsUrl, EmbeddedRouteView } from './Routes';
import { getCachedCoords, requestCoords } from '../utils/geolocation';
import { isMobileSharedLink, openInApp } from '../utils/openInApp';
import ReportBlockMenu from '../components/social/ReportBlockMenu';
import ExpandingTextarea from '../components/common/ExpandingTextarea';
import { EVENT_AESTHETICS, getEventAesthetic } from '../components/eventAesthetics/registry';
import AestheticEventCard from '../components/eventAesthetics/AestheticEventCard';
import AestheticDetailBanner from '../components/eventAesthetics/AestheticDetailBanner';
import AestheticSwatch from '../components/eventAesthetics/AestheticSwatch';
import EventAestheticPicker from '../components/eventAesthetics/EventAestheticPicker';
import LoadError from '../components/common/LoadError';
import { eventBannerThumb } from '../utils/media';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const EVENT_TYPES = ['Event', 'Meet', 'Cars & Coffee', 'Cruise', 'Track Day', 'Roll Racing', 'Show & Shine', 'Other'];

const SORT_OPTIONS = [
  { key: 'featured', label: 'Featured', icon: '✦' },
  { key: 'nearest',  label: 'Nearest',  icon: '◎' },
  { key: 'soonest',  label: 'Soonest',  icon: '◷' },
  { key: 'popular',  label: 'Popular',  icon: '★' },
];

const WHEN_OPTIONS = [
  { key: 'today',    label: 'Today / Tonight' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'weekend',  label: 'This Weekend' },
];

// Returns [start, end] Date objects for a given "when" key, relative to now.
// Weekend covers Fri 00:00 → Sun 23:59 of the current week (or the upcoming one
// when today is Mon–Thu).
const whenWindow = (key, now = new Date()) => {
  if (key === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    const end = new Date(now); end.setHours(23, 59, 59, 999);
    return [start, end];
  }
  if (key === 'tomorrow') {
    const start = new Date(now); start.setDate(start.getDate() + 1); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setHours(23, 59, 59, 999);
    return [start, end];
  }
  if (key === 'weekend') {
    const dow = now.getDay(); // 0=Sun, 5=Fri, 6=Sat
    let friOffset;
    if (dow === 0)      friOffset = -2; // Sun → Fri was 2 days ago
    else if (dow === 6) friOffset = -1; // Sat → Fri was 1 day ago
    else if (dow === 5) friOffset = 0;  // Fri
    else                friOffset = 5 - dow; // Mon–Thu → upcoming Fri
    const start = new Date(now); start.setDate(start.getDate() + friOffset); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + 2); end.setHours(23, 59, 59, 999);
    return [start, end];
  }
  return [null, null];
};

const matchesWhen = (whenKey, eventDate) => {
  if (!whenKey) return true;
  if (!eventDate) return false;
  const [start, end] = whenWindow(whenKey);
  if (!start || !end) return true;
  const d = new Date(eventDate);
  return d >= start && d <= end;
};

// Haversine distance in km between two lat/lon points
const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Proximity curve: slow decline within 100 km, steep exponential drop after.
// 0 km: 1.00 · 100 km: 0.70 · 150 km: 0.37 · 300 km: 0.06 · 1000+ km: ~0.
const proximityScore = (distanceKm) => {
  if (distanceKm <= 100) return 1 - 0.3 * (distanceKm / 100);
  return 0.7 * Math.exp(-(distanceKm - 100) / 80);
};

const featuredScore = (event, userCoords) => {
  const attendees = Math.log((event.attendeeCount || 0) + 1) / Math.log(51);
  const hasImage = event.bannerImageUrl ? 1 : 0;
  const daysUntil = event.eventDate
    ? (new Date(event.eventDate) - Date.now()) / 86400000
    : 30;
  const timeliness = daysUntil < 0
    ? 0
    : daysUntil <= 14
      ? 0.7 + (1 - daysUntil / 14) * 0.3
      : Math.max(0, 1 - (daysUntil - 14) / 60);
  const comments = Math.log((event.commentCount || 0) + 1) / Math.log(11);
  const base = attendees * 0.40 + timeliness * 0.35 + comments * 0.15 + hasImage * 0.10;

  // Multiplicative proximity factor with a 0.10 floor so distant events still
  // appear, just buried. Events with no coords are treated as neutral (×1).
  if (userCoords && event.latitude != null && event.longitude != null) {
    const d = haversine(userCoords.lat, userCoords.lon, parseFloat(event.latitude), parseFloat(event.longitude));
    return base * Math.max(0.10, proximityScore(d));
  }
  return base;
};

const sortEvents = (events, sortBy, userCoords) => {
  const arr = [...events];
  switch (sortBy) {
    case 'featured': return arr.sort((a, b) => featuredScore(b, userCoords) - featuredScore(a, userCoords));
    case 'nearest':
      if (!userCoords) return arr;
      return arr.sort((a, b) => {
        const da = (a.latitude != null && a.longitude != null) ? haversine(userCoords.lat, userCoords.lon, parseFloat(a.latitude), parseFloat(a.longitude)) : Infinity;
        const db = (b.latitude != null && b.longitude != null) ? haversine(userCoords.lat, userCoords.lon, parseFloat(b.latitude), parseFloat(b.longitude)) : Infinity;
        return da - db;
      });
    case 'soonest':  return arr.sort((a, b) => new Date(a.eventDate || 0) - new Date(b.eventDate || 0));
    case 'popular':  return arr.sort((a, b) => (b.attendeeCount || 0) - (a.attendeeCount || 0));
    default: return arr;
  }
};

const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+\.[a-z]{2,}[^\s]*)/gi;
const TRAILING_PUNCT = /[.,!?;:)\]}>]+$/;
const splitTextWithLinks = (text) => {
  if (!text) return [];
  const out = [];
  let last = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    let url = m[1];
    let trailing = '';
    const trim = url.match(TRAILING_PUNCT);
    if (trim) { trailing = trim[0]; url = url.slice(0, -trailing.length); }
    if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
    out.push({ type: 'link', value: url });
    if (trailing) out.push({ type: 'text', value: trailing });
    last = m.index + m[1].length;
  }
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out;
};
const renderWithLinks = (text) => splitTextWithLinks(text).map((p, i) => {
  if (p.type === 'link') {
    const href = p.value.startsWith('http') ? p.value : `https://${p.value}`;
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>{p.value}</a>;
  }
  return p.value;
});

const categoryColor = {
  'Event':        '#4a9eff',
  'Cruise':      '#7c6ff7',
  'Track Day':   '#e84545',
  'Roll Racing': '#f5a623',
  'Show & Shine':'#4caf7d',
  'Meet':        '#ff7c3f',
  // Cream — the only light category colour. Fine as badge text/border on the
  // dark glass; the map pin fills with it, so that pin gets a dark centre dot.
  'Cars & Coffee': '#ecdfc4',
  // Legacy: events created before 'Car Meet' was renamed to 'Meet'. Kept so they
  // keep their colour until server/scripts/retagCarMeet.js cleans the DB.
  'Car Meet':    '#ff7c3f',
  'Other':       '#8892b0',
};

// An event's tags as an array: the new multi-tag `tags` field when present,
// otherwise the legacy single `category` folded into a one-element list. The
// first tag is the "primary" one and drives the card accent colour.
const eventTags = (event) => {
  if (Array.isArray(event?.tags) && event.tags.length) return event.tags;
  if (event?.category) return [event.category];
  return [];
};
const primaryTagColor = (event) => categoryColor[eventTags(event)[0]] || categoryColor['Other'];

const CalIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

const PeopleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

// Event tab icons — mirror the app's Ionicons (info / location / car / chat / gallery / route)
const InfoTabIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const LocationTabIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

const CarTabIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2" /><circle cx="6.5" cy="16.5" r="2.5" /><circle cx="16.5" cy="16.5" r="2.5" />
  </svg>
);

const ChatTabIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const GalleryTabIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
  </svg>
);

const RouteTabIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const ChatEmptyIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const formatTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
};

// ─── Calendar helpers ─────────────────────────────────────────────────────────
const toGCalDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

const toGCalUrl = (ev) => {
  const start = toGCalDate(ev.eventDate);
  const end = ev.endDate ? toGCalDate(ev.endDate) : (ev.eventDate ? toGCalDate(new Date(new Date(ev.eventDate).getTime() + 2 * 3600 * 1000).toISOString()) : '');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.title || '',
    dates: `${start}/${end}`,
    details: ev.description || '',
    location: ev.address || '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const downloadICS = (ev) => {
  const fmt = (iso) => toGCalDate(iso);
  const start = ev.eventDate ? fmt(ev.eventDate) : fmt(new Date().toISOString());
  const end = ev.endDate ? fmt(ev.endDate) : fmt(new Date(new Date(ev.eventDate || Date.now()).getTime() + 2 * 3600 * 1000).toISOString());
  const esc = (s) => (s || '').replace(/\n/g, '\\n').replace(/,/g, '\\,');
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//MyCarCollection//EN',
    'BEGIN:VEVENT',
    `SUMMARY:${esc(ev.title)}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `DESCRIPTION:${esc(ev.description)}`,
    `LOCATION:${esc(ev.address)}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${(ev.title || 'event').replace(/\s+/g, '-')}.ics`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Location Autocomplete ────────────────────────────────────────────────────
const LocationInput = ({ value, onChange, onSelect }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef();
  const wrapperRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const token = process.env.REACT_APP_MAPBOX_TOKEN;
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${token}&autocomplete=true&limit=6&types=address,place,locality,neighborhood`
        );
        const data = await res.json();
        const features = data.features || [];
        setResults(features);
        setOpen(features.length > 0);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 400);
  };

  const handleSelect = (place) => {
    const label = place.place_name;
    const [lon, lat] = place.geometry.coordinates;
    onChange(label);
    onSelect({ address: label, latitude: lat, longitude: lon });
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          className="event-form-input"
          value={value}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Start typing an address or suburb…"
          autoComplete="off"
        />
        {searching && (
          <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          </div>
        )}
      </div>
      {open && results.length > 0 && (
        <ul style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#1a2030', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6, zIndex: 2000, listStyle: 'none', margin: 0, padding: 0,
          maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          {results.map((place, i) => (
            <li
              key={i}
              onMouseDown={() => handleSelect(place)}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: '0.82rem',
                color: '#c9ced6', borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {place.place_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Date helpers ─────────────────────────────────────────────────────────────
// <input type="datetime-local"> uses unzoned strings like "2026-05-08T17:30".
// Postgres TIMESTAMPTZ treats unzoned strings as UTC, so we have to convert
// to/from local explicitly — otherwise events display shifted by your
// timezone offset (e.g. AEST 17:30 ends up showing as 03:30 next day).
const localInputToISO = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d.toISOString();
};
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
// Add hours to a datetime-local input string. Returns the same format.
const addHoursToInput = (val, hours) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  d.setHours(d.getHours() + hours);
  return isoToLocalInput(d.toISOString());
};

// Build datetime-local presets relative to "now" so the user doesn't have
// to fiddle with a date+time picker for common cases.
const buildDatePresets = () => {
  const now = new Date();
  const at = (offsetDays, hour, minute = 0) => {
    const d = new Date(now);
    d.setDate(d.getDate() + offsetDays);
    d.setHours(hour, minute, 0, 0);
    return isoToLocalInput(d.toISOString());
  };
  // Days until next Saturday (or Sat itself if it's already in the future today).
  const daysToWeekday = (target) => {
    const offset = (target - now.getDay() + 7) % 7;
    return offset === 0 ? 7 : offset; // never "today"
  };
  return [
    { label: 'Tonight, 7 PM',        value: at(now.getHours() < 19 ? 0 : 1, 19) },
    { label: 'Tomorrow, 7 PM',       value: at(1, 19) },
    { label: 'This Saturday, 5 PM',  value: at(daysToWeekday(6), 17) },
    { label: 'This Sunday, 11 AM',   value: at(daysToWeekday(0), 11) },
  ];
};

// Pretty-print a duration in hours/minutes from two datetime-local inputs.
const formatDuration = (start, end) => {
  if (!start || !end) return '';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms) || ms <= 0) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hrs = mins / 60;
  if (hrs % 1 === 0) return `${hrs}h`;
  const h = Math.floor(hrs);
  const m = mins % 60;
  return `${h}h ${m}m`;
};

// ─── Time picker ─────────────────────────────────────────────────────────────
// 30-minute slots, "HH:mm" 24h values rendered as friendly 12h labels.
const TIME_SLOTS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return out;
})();

const formatTime12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

// Custom dropdown for picking a time — avoids the clunky native <input type="time">.
const TimeSelect = ({ value, onChange, style }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const selectedRef = useRef();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center' });
    }
  }, [open]);

  return (
    <div className="time-select" ref={ref} style={style}>
      <button
        type="button"
        className={`event-form-input time-select-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className={value ? '' : 'time-select-placeholder'}>{value ? formatTime12(value) : 'Select time'}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className="time-select-menu">
          {TIME_SLOTS.map(t => (
            <button
              key={t}
              ref={t === value ? selectedRef : null}
              type="button"
              className={`time-select-option${t === value ? ' selected' : ''}`}
              onClick={() => { onChange(t); setOpen(false); }}
            >
              {formatTime12(t)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Geocoding + map-pin helpers (mirror of Landmarks.jsx) ───────────────────
const geocodeSearch = async (q) => {
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&country=au`
    );
    const data = await res.json();
    return data.features || [];
  } catch { return []; }
};

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&limit=1`
    );
    const data = await res.json();
    return data.features?.[0]?.place_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch { return `${lat.toFixed(5)}, ${lng.toFixed(5)}`; }
};

const eventPinIcon = L.divIcon({
  className: '',
  html: `<div style="
    width:28px;height:28px;border-radius:50% 50% 50% 0;
    background:#4a9eff;border:2px solid #fff;
    transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;
    box-shadow:0 2px 8px rgba(0,0,0,0.5);
  "><span style="transform:rotate(45deg);color:#fff;font-size:14px;line-height:1;">●</span></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Search input used inside the location step. Mirrors Landmarks.jsx SearchInput.
const EventLocationSearchInput = ({ value, onChange, onSelect, placeholder }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const wrapperRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    onChange(q);
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const features = await geocodeSearch(q);
      setResults(features);
      setOpen(features.length > 0);
      setLoading(false);
    }, 380);
  };

  const handleSelect = (place) => {
    const [lon, lat] = place.geometry.coordinates;
    onChange(place.place_name);
    onSelect({ address: place.place_name, latitude: lat, longitude: lon });
    setResults([]); setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flex: 1 }}>
      <div style={{ position: 'relative' }}>
        <input
          className="event-form-input"
          value={value}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={(e) => {
            // Enter should pick the top suggestion (or do nothing) — never
            // submit the form / create the event from the location field.
            if (e.key === 'Enter') {
              e.preventDefault();
              if (open && results.length > 0) handleSelect(results[0]);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {loading && <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}><div className="loading-spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /></div>}
      </div>
      {open && results.length > 0 && (
        <ul style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#1a2030', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, zIndex: 3000, listStyle: 'none', margin: 0, padding: 0, maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
          {results.map((place, i) => (
            <li key={i} onMouseDown={() => handleSelect(place)}
              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '0.81rem', color: '#c9ced6', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >{place.place_name}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Inline cruise-path builder ───────────────────────────────────────────────
// A cruise event can carry a driving path stored directly on the event (NOT a
// row in the routes table). The event's own location is the START; the builder
// below adds an END and optional stops, then draws + measures the drive with
// Mapbox Directions — the same machinery Routes.jsx uses. The path is saved as
// `event.cruisePath` and rendered on the detail view exactly like a route,
// including "Open in Google Maps".

const PinDropSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
);

// Route-style A/B/stop pins for the cruise builder (mirror Routes.jsx makeIcon).
const makeCruiseIcon = (color, letter) => L.divIcon({
  className: '',
  html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:${color};border:2px solid #fff;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.5);"><span style="transform:rotate(45deg);color:#fff;font-size:11px;font-weight:700;line-height:1;">${letter}</span></div>`,
  iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -28],
});
const cruiseStartIcon = makeCruiseIcon('#4caf7d', 'A');
const cruiseEndIcon   = makeCruiseIcon('#e84545', 'B');
const cruiseStopIcon  = (n) => makeCruiseIcon('#f5a623', n);

const ALT_COLORS = ['#1a73e8', '#f5a623', '#4caf7d', '#e06fa0'];

// Mapbox Directions — returns alternative driving routes for the given points.
const fetchDirections = async (points, avoidMotorway = false) => {
  if (points.length < 2) return null;
  const coords = points.map(p => `${p.longitude},${p.latitude}`).join(';');
  const exclude = avoidMotorway ? '&exclude=motorway' : '';
  try {
    const res = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/driving/${coords}?geometries=geojson&alternatives=true&overview=full${exclude}&access_token=${MAPBOX_TOKEN}`
    );
    const data = await res.json();
    if (!data.routes?.length) return null;
    return data.routes;
  } catch { return null; }
};

// Is an event form currently describing a cruise path? True once the builder is
// opened (form.cruiseOpen) or any end / stop data exists. Used to hide the
// "attach a saved route" section (the two are mutually exclusive).
const cruiseActiveForm = (form) => !!(form.cruiseOpen || form.endLatitude || form.endInput || (form.stops && form.stops.length));

// Build the `cruisePath` payload sent to the API (null when there's no end).
const buildCruisePayload = (form) => {
  if (!form.endLatitude || !form.endLongitude) return null;
  return {
    endAddress: form.endAddress || form.endInput || null,
    endLatitude: form.endLatitude,
    endLongitude: form.endLongitude,
    waypoints: (form.stops || [])
      .filter(s => s.latitude && s.longitude)
      .map(s => ({ address: s.address, latitude: s.latitude, longitude: s.longitude })),
    routeGeometry: form.cruiseGeometry || null,
    distanceKm: form.cruiseDistanceKm ? parseFloat(form.cruiseDistanceKm) : null,
  };
};

// Turn a saved event.cruisePath into a route-shaped object so routeStaticMapUrl /
// buildGoogleMapsUrl (and the points list) can treat it exactly like a route.
// The event's own location is the start.
const cruiseRouteShape = (event) => {
  const cp = event.cruisePath;
  if (!cp || cp.endLatitude == null) return null;
  return {
    name: null, tags: [],
    startAddress: event.address, startLatitude: event.latitude, startLongitude: event.longitude,
    endAddress: cp.endAddress, endLatitude: cp.endLatitude, endLongitude: cp.endLongitude,
    waypoints: cp.waypoints || [],
    routeGeometry: cp.routeGeometry || null,
    distanceKm: cp.distanceKm || null,
  };
};

// Map internals for the cruise builder (must live inside MapContainer). Draws the
// event location as the start, plus stop + end markers and the directions lines.
const CruiseMapController = ({ form, cruiseActive, activePlacingRef, onPlace, dirRoutes, selectedRouteIdx, setSelectedRouteIdx }) => {
  const map = useMap();

  useMapEvents({
    click(e) {
      const placing = activePlacingRef.current;
      if (placing === null || placing === undefined) return;
      onPlace(placing, e.latlng.lat, e.latlng.lng);
    },
  });

  const stopsLen = (form.stops || []).length;
  useEffect(() => {
    const pts = [];
    if (form.latitude && form.longitude) pts.push([form.latitude, form.longitude]);
    (form.stops || []).forEach(s => { if (s.latitude && s.longitude) pts.push([s.latitude, s.longitude]); });
    if (form.endLatitude && form.endLongitude) pts.push([form.endLatitude, form.endLongitude]);
    if (pts.length === 1) map.flyTo(pts[0], 13, { animate: true, duration: 0.8 });
    else if (pts.length >= 2) map.flyToBounds(L.latLngBounds(pts), { padding: [50, 50], animate: true, duration: 0.8 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.latitude, form.longitude, form.endLatitude, form.endLongitude, stopsLen]);

  return (
    <>
      {cruiseActive && dirRoutes.map((r, i) => i !== selectedRouteIdx && (
        <Polyline key={`alt-${i}`} positions={r.geometry.coordinates.map(([ln, lt]) => [lt, ln])}
          pathOptions={{ color: ALT_COLORS[i] || '#888', weight: 4, lineCap: 'round', opacity: 0.45, dashArray: '6 5' }}
          eventHandlers={{ click: () => setSelectedRouteIdx(i) }}
        />
      ))}
      {cruiseActive && dirRoutes[selectedRouteIdx] && (
        <Polyline positions={dirRoutes[selectedRouteIdx].geometry.coordinates.map(([ln, lt]) => [lt, ln])}
          pathOptions={{ color: ALT_COLORS[selectedRouteIdx] || '#1a73e8', weight: 5, lineCap: 'round', opacity: 1 }}
        />
      )}
      {form.latitude && form.longitude && (
        <Marker position={[form.latitude, form.longitude]} icon={cruiseActive ? cruiseStartIcon : eventPinIcon} draggable
          eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onPlace('start', lat, lng); } }}
        />
      )}
      {cruiseActive && (form.stops || []).map((s, i) => s.latitude && s.longitude && (
        <Marker key={`stop-${i}`} position={[s.latitude, s.longitude]} icon={cruiseStopIcon(i + 1)} draggable
          eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onPlace(i, lat, lng); } }}
        />
      ))}
      {cruiseActive && form.endLatitude && form.endLongitude && (
        <Marker position={[form.endLatitude, form.endLongitude]} icon={cruiseEndIcon} draggable
          eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onPlace('end', lat, lng); } }}
        />
      )}
    </>
  );
};

// The full location step for the event modals: the meet-point (start) field, an
// optional "+ Add end location" cruise builder (end + stops + directions), and
// the map. `routeAttached` hides the cruise builder when a saved route is
// attached instead (the two are mutually exclusive).
const EventLocationStep = ({ form, setForm, routeAttached }) => {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [activePlacing, setActivePlacingState] = useState(null); // 'start' | 'end' | stopIndex
  const activePlacingRef = useRef(null);
  const setActivePlacing = (val) => {
    const v = typeof val === 'function' ? val(activePlacingRef.current) : val;
    activePlacingRef.current = v;
    setActivePlacingState(v);
  };
  const [dirRoutes, setDirRoutes] = useState([]);
  const [selectedRouteIdx, setSelectedRouteIdx] = useState(0);
  const [avoidMotorway, setAvoidMotorway] = useState(false);
  const dirFetchRef = useRef();

  // `cruiseOpen` lives on the form so the parent modal can hide its
  // "attach a saved route" section the moment the builder is opened.
  const showCruise = form.cruiseOpen && !routeAttached;

  const handlePlace = useCallback(async (key, lat, lng) => {
    setActivePlacing(null);
    const address = await reverseGeocode(lat, lng);
    if (key === 'start') {
      setForm(f => ({ ...f, latitude: lat, longitude: lng, address, locationInput: address }));
    } else if (key === 'end') {
      setForm(f => ({ ...f, endLatitude: lat, endLongitude: lng, endAddress: address, endInput: address }));
    } else {
      setForm(f => {
        const stops = [...(f.stops || [])];
        stops[key] = { ...stops[key], latitude: lat, longitude: lng, address, input: address };
        return { ...f, stops };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setForm]);

  // Recompute the drawn path + distance whenever start / end / stops change.
  const triggerDirections = useCallback(async (f, avoid) => {
    const pts = [];
    if (f.latitude && f.longitude) pts.push({ latitude: f.latitude, longitude: f.longitude });
    (f.stops || []).forEach(s => { if (s.latitude && s.longitude) pts.push({ latitude: s.latitude, longitude: s.longitude }); });
    if (f.endLatitude && f.endLongitude) pts.push({ latitude: f.endLatitude, longitude: f.endLongitude });
    if (pts.length < 2) { setDirRoutes([]); return; }
    const routes = await fetchDirections(pts, avoid);
    if (routes) {
      setDirRoutes(routes);
      setSelectedRouteIdx(0);
      const distM = routes[0].distance;
      setForm(prev => ({ ...prev, cruiseDistanceKm: (distM / 1000).toFixed(1), cruiseGeometry: routes[0].geometry }));
    }
  }, [setForm]);

  useEffect(() => {
    clearTimeout(dirFetchRef.current);
    dirFetchRef.current = setTimeout(() => triggerDirections(form, avoidMotorway), 600);
    return () => clearTimeout(dirFetchRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.latitude, form.longitude, form.endLatitude, form.endLongitude, form.stops, avoidMotorway]);

  const handleSelectRoute = (idx) => {
    setSelectedRouteIdx(idx);
    const distM = dirRoutes[idx].distance;
    setForm(f => ({ ...f, cruiseDistanceKm: (distM / 1000).toFixed(1), cruiseGeometry: dirRoutes[idx].geometry }));
  };

  const addStop = () => setForm(f => ({ ...f, stops: [...(f.stops || []), { id: Date.now(), input: '', address: '', latitude: null, longitude: null }] }));
  const removeStop = (i) => setForm(f => ({ ...f, stops: (f.stops || []).filter((_, idx) => idx !== i) }));
  const cancelCruise = () => {
    setDirRoutes([]);
    setActivePlacing(null);
    setForm(f => ({ ...f, cruiseOpen: false, endInput: '', endAddress: '', endLatitude: null, endLongitude: null, stops: [], cruiseGeometry: null, cruiseDistanceKm: '' }));
  };

  const placingLabel = activePlacing === 'start' ? (showCruise ? 'start' : 'location')
    : activePlacing === 'end' ? 'end'
    : typeof activePlacing === 'number' ? `stop ${activePlacing + 1}` : null;

  return (
    <>
      <div className="route-location-inputs">
        {/* Start = the event / meet-point location */}
        <div className="route-location-row">
          <div className="route-location-dot" style={{ background: showCruise ? '#4caf7d' : '#4a9eff' }} />
          <EventLocationSearchInput
            value={form.locationInput}
            onChange={v => set('locationInput', v)}
            onSelect={({ address, latitude, longitude }) => setForm(f => ({ ...f, address, latitude, longitude, locationInput: address }))}
            placeholder={showCruise ? 'Start location (meet point)…' : 'Search for a location…'}
          />
          <button type="button" className={`route-pin-btn${activePlacing === 'start' ? ' active' : ''}`} title="Click map to place pin" onClick={() => setActivePlacing(p => p === 'start' ? null : 'start')}>
            <PinDropSvg />
          </button>
        </div>

        {showCruise && (
          <>
            {(form.stops || []).map((stop, i) => (
              <div key={stop.id} className="route-location-row">
                <div className="route-location-dot" style={{ background: '#f5a623' }} />
                <EventLocationSearchInput
                  value={stop.input}
                  onChange={v => setForm(f => { const stops = [...f.stops]; stops[i] = { ...stops[i], input: v }; return { ...f, stops }; })}
                  onSelect={({ address, latitude, longitude }) => setForm(f => { const stops = [...f.stops]; stops[i] = { ...stops[i], address, latitude, longitude, input: address }; return { ...f, stops }; })}
                  placeholder={`Stop ${i + 1}…`}
                />
                <button type="button" className={`route-pin-btn${activePlacing === i ? ' active' : ''}`} title="Click map to place pin" onClick={() => setActivePlacing(p => p === i ? null : i)}>
                  <PinDropSvg />
                </button>
                <button type="button" className="route-remove-stop" onClick={() => removeStop(i)}>✕</button>
              </div>
            ))}

            {/* End */}
            <div className="route-location-row">
              <div className="route-location-dot" style={{ background: '#e84545' }} />
              <EventLocationSearchInput
                value={form.endInput}
                onChange={v => set('endInput', v)}
                onSelect={({ address, latitude, longitude }) => setForm(f => ({ ...f, endAddress: address, endLatitude: latitude, endLongitude: longitude, endInput: address }))}
                placeholder="End location…"
              />
              <button type="button" className={`route-pin-btn${activePlacing === 'end' ? ' active' : ''}`} title="Click map to place pin" onClick={() => setActivePlacing(p => p === 'end' ? null : 'end')}>
                <PinDropSvg />
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" className="route-add-stop-btn" onClick={addStop}>+ Add Stop</button>
              <button type="button" className="event-route-inline-btn" style={{ marginLeft: 'auto' }} onClick={cancelCruise}>Remove route</button>
            </div>
          </>
        )}

        {!showCruise && !routeAttached && (
          <button type="button" className="route-add-stop-btn" onClick={() => set('cruiseOpen', true)}>+ Add end location (make it a cruise)</button>
        )}
      </div>

      {/* Map */}
      <div className="route-map-container" style={{ cursor: activePlacing !== null ? 'crosshair' : 'grab' }}>
        {placingLabel && (
          <div className="route-placing-hint">Click anywhere on the map to place the <strong>{placingLabel}</strong> pin</div>
        )}
        <MapContainer center={[-25.2744, 133.7751]} zoom={4} style={{ width: '100%', height: '100%' }} zoomControl={true} zoomAnimation={false} fadeAnimation={false}>
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
            tileSize={512}
            zoomOffset={-1}
            attribution='&copy; Mapbox'
          />
          <CruiseMapController
            form={form}
            cruiseActive={showCruise}
            activePlacingRef={activePlacingRef}
            onPlace={handlePlace}
            dirRoutes={dirRoutes}
            selectedRouteIdx={selectedRouteIdx}
            setSelectedRouteIdx={handleSelectRoute}
          />
        </MapContainer>
      </div>

      {showCruise && dirRoutes.length > 0 && (
        <div className="route-alternatives">
          <div className="route-alt-label">Route options:</div>
          {dirRoutes.map((r, i) => {
            const fastestDur = dirRoutes[0].duration;
            const extraMin = Math.round((r.duration - fastestDur) / 60);
            const label = i === 0 ? 'Fastest' : extraMin > 0 ? `+${extraMin} min` : 'Alternative';
            const color = ALT_COLORS[i] || '#888';
            return (
              <button key={i} type="button" className={`route-alt-btn${selectedRouteIdx === i ? ' active' : ''}`}
                style={selectedRouteIdx === i ? { borderColor: color, color } : {}}
                onClick={() => handleSelectRoute(i)}>
                <span className="route-alt-swatch" style={{ background: color }} />
                {label} — {(r.distance / 1000).toFixed(1)} km · {Math.round(r.duration / 60)} min
              </button>
            );
          })}
          <button type="button" className={`route-alt-btn route-alt-scenic-toggle${avoidMotorway ? ' active' : ''}`}
            style={avoidMotorway ? { borderColor: '#4caf7d', color: '#4caf7d', marginLeft: 'auto' } : { marginLeft: 'auto' }}
            onClick={() => setAvoidMotorway(v => !v)}>
            <span className="route-alt-scenic-main">{avoidMotorway ? 'Scenic on' : 'Scenic'}</span>
            <span className="route-alt-scenic-sub">avoids motorways</span>
          </button>
        </div>
      )}

      {showCruise && form.cruiseDistanceKm && (
        <div className="route-distance-display">
          <strong>{form.cruiseDistanceKm} km</strong> · distance auto-calculated from route
        </div>
      )}
    </>
  );
};

// Renders a saved inline cruise path on the event detail view — static map,
// start → stops → end, distance, and "Open in Google Maps" (same as a route).
const EmbeddedCruiseView = ({ event }) => {
  const route = cruiseRouteShape(event);
  if (!route) return null;
  const mapImg = routeStaticMapUrl(route);
  const googleMapsUrl = buildGoogleMapsUrl(route);
  return (
    <div className="route-tab-details">
      {mapImg && (
        <div className="route-detail-map-top" style={{ borderRadius: 10, overflow: 'hidden' }}>
          <img src={mapImg} alt="Cruise route" />
        </div>
      )}
      <div className="route-detail-points" style={{ marginTop: 14 }}>
        <div className="route-detail-point">
          <div className="route-detail-point-dot route-detail-point-dot--start" />
          <div><div className="route-detail-point-label">Start</div><div className="route-detail-point-address">{route.startAddress || 'Meet point'}</div></div>
        </div>
        {(route.waypoints || []).map((wp, i) => (
          <React.Fragment key={i}>
            <div className="route-detail-connector" />
            <div className="route-detail-point">
              <div className="route-detail-point-dot" style={{ background: '#f5a623' }} />
              <div><div className="route-detail-point-label">Stop {i + 1}</div><div className="route-detail-point-address">{wp.address || 'Unknown'}</div></div>
            </div>
          </React.Fragment>
        ))}
        <div className="route-detail-connector" />
        <div className="route-detail-point">
          <div className="route-detail-point-dot route-detail-point-dot--end" />
          <div><div className="route-detail-point-label">End</div><div className="route-detail-point-address">{route.endAddress || 'Unknown'}</div></div>
        </div>
      </div>
      {route.distanceKm && (
        <div className="route-distance-display" style={{ marginTop: 10 }}>
          <strong>{parseFloat(route.distanceKm).toFixed(1)} km</strong>
        </div>
      )}
      {googleMapsUrl && (
        <div className="route-detail-actions">
          <a className="btn-primary" href={googleMapsUrl} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', padding: '9px 18px', fontSize: '0.85rem', fontWeight: 600 }}>
            Open in Google Maps ↗
          </a>
        </div>
      )}
    </div>
  );
};

// ─── Invite picker (private events) — mirrors the group-chat people picker ────
const EventInvitePicker = ({ selected, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/messages/following').then(({ data }) => setFollowing(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/messages/search-users', { params: { q: query.trim() } });
        setResults(data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const list = query.trim() ? results : following;
  const isSel = (id) => selected.some(u => u.id === id);
  const toggle = (u) => {
    if (isSel(u.id)) onChange(selected.filter(s => s.id !== u.id));
    else onChange([...selected, u]);
  };

  return (
    <div className="event-invite-picker">
      {selected.length > 0 && (
        <div className="event-invite-chips">
          {selected.map(u => (
            <span key={u.id} className="event-invite-chip">
              {u.username}
              <button type="button" onClick={() => toggle(u)} aria-label="Remove">✕</button>
            </span>
          ))}
        </div>
      )}
      <input
        className="event-form-input"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search people to invite…"
      />
      <div className="event-invite-list">
        {!query.trim() && following.length === 0 && <p className="event-invite-empty">Follow people or search by username to invite them.</p>}
        {query.trim() && loading && <p className="event-invite-empty">Searching…</p>}
        {query.trim() && !loading && results.length === 0 && <p className="event-invite-empty">No users found.</p>}
        {list.map(u => (
          <button type="button" key={u.id} className={`event-invite-row${isSel(u.id) ? ' selected' : ''}`} onClick={() => toggle(u)}>
            <div className="event-invite-avatar">
              {u.profileImageUrl ? <img src={u.profileImageUrl} alt="" /> : (u.username?.[0] || '?').toUpperCase()}
            </div>
            <span className="event-invite-username">{u.username}</span>
            {isSel(u.id) && <span className="event-invite-check">✓</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Create Event Modal ───────────────────────────────────────────────────────
const CreateEventModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({
    title: '', tags: [], description: '',
    locationInput: '', address: '', latitude: null, longitude: null,
    // Inline cruise path — the meet-point location above is the start.
    cruiseOpen: false,
    endInput: '', endAddress: '', endLatitude: null, endLongitude: null,
    stops: [], cruiseGeometry: null, cruiseDistanceKm: '',
    eventDate: '', endDate: '', maxAttendees: '',
    aesthetic: null,
  });
  const [imageFile, setImageFile] = useState(null);
  const [showAesthetic, setShowAesthetic] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0); // 0 = visibility, 1 = details, 2 = location
  const [visibility, setVisibility] = useState(null); // 'public' | 'private'
  const [invitees, setInvitees] = useState([]); // [{ id, username, profileImageUrl }]
  const [selectedRoute, setSelectedRoute] = useState(null); // { id, name, ... }
  const [routeMode, setRouteMode] = useState('none'); // 'none' | 'search'
  const [routeSearch, setRouteSearch] = useState('');
  const [routeResults, setRouteResults] = useState([]);
  const [routeSearchLoading, setRouteSearchLoading] = useState(false);
  const routeSearchRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTag = (t) => setForm(f => ({
    ...f,
    tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t],
  }));

  // Server-side route search. Loads a list as soon as search mode opens (empty
  // query → most recent routes), then narrows as the organiser types.
  useEffect(() => {
    if (routeMode !== 'search') return;
    const t = setTimeout(async () => {
      setRouteSearchLoading(true);
      try {
        const { data } = await api.get('/routes', { params: { search: routeSearch.trim(), includeMine: true, limit: 20 } });
        setRouteResults(data.routes || []);
      } catch { setRouteResults([]); }
      finally { setRouteSearchLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [routeSearch, routeMode]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');
    // Pressing Enter on step 1 advances rather than submitting.
    if (step !== 2) { setStep(2); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/events', {
        title: form.title, tags: form.tags, description: form.description,
        address: form.address || form.locationInput || null,
        latitude: form.latitude, longitude: form.longitude,
        eventDate: localInputToISO(form.eventDate),
        endDate: localInputToISO(form.endDate),
        maxAttendees: form.maxAttendees ? parseInt(form.maxAttendees) : null,
        aesthetic: form.aesthetic || null,
        // Attaching a saved route and building an inline cruise path are mutually
        // exclusive — the route wins if somehow both are set.
        routeId: selectedRoute?.id || null,
        cruisePath: selectedRoute ? null : buildCruisePayload(form),
        isPublic: visibility !== 'private',
        invites: visibility === 'private' ? invitees.map(u => u.id) : [],
      });
      let finalEvent = data.event;
      if (imageFile) {
        const fd = new FormData();
        fd.append('banner', imageFile);
        const { data: bannerData } = await api.post(`/events/${finalEvent.id}/banner`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        finalEvent = { ...finalEvent, bannerImageUrl: bannerData.bannerImageUrl };
      }
      onCreate(finalEvent);
      toast.success('Event created!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create event');
    } finally { setSubmitting(false); }
  };

  const goNext = () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setStep(2);
  };

  const isMapStep = step === 2;

  return (
    <>
    <div className="event-modal-overlay">
      <div className={`event-modal${isMapStep ? ' event-modal--map' : ''}`}>
        <div
          className="event-modal-header"
          style={isMapStep
            ? { padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }
            : undefined}
        >
          <h2 className="event-modal-title">{step === 0 ? 'Create Event' : step === 1 ? 'Event Details' : 'Set Location'}</h2>
          <button className="event-modal-close" onClick={onClose}>✕</button>
        </div>

        <form
          onSubmit={submit}
          onKeyDown={(e) => {
            // Pressing Enter in a text field should never create the event —
            // only the explicit "Next" / "Create Event" buttons advance the
            // flow. (Enter on a focused button still activates it normally.)
            if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault();
          }}
          className="event-modal-form"
          style={isMapStep
            ? { flex: 1, minHeight: 0, padding: 0, gap: 0 }
            : undefined}
        >

          {/* ── Step 0: Public or Private ── */}
          {step === 0 && (
            <>
              <div className="event-visibility-choices">
                <button
                  type="button"
                  className={`event-visibility-card${visibility === 'public' ? ' active' : ''}`}
                  onClick={() => { setVisibility('public'); setStep(1); }}
                >
                  <span className="event-visibility-card-title">Public</span>
                  <span className="event-visibility-card-desc">Anyone can find it in the events list and on the map.</span>
                </button>
                <button
                  type="button"
                  className={`event-visibility-card${visibility === 'private' ? ' active' : ''}`}
                  onClick={() => { setVisibility('private'); setStep(1); }}
                >
                  <span className="event-visibility-card-title">Private</span>
                  <span className="event-visibility-card-desc">Invite-only. Hidden from everyone except the people you invite.</span>
                </button>
              </div>
              <div className="event-modal-actions">
                <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              </div>
            </>
          )}

          {/* ── Step 1: Details ── */}
          {step === 1 && (
            <>
              <div className="event-form-row">
                <label className="event-form-label">Photo</label>
                <ImageUploadField preview={imagePreview} onChange={handleImageChange} />
              </div>
              <div className="event-form-row">
                <label className="event-form-label">Event Aesthetic</label>
                <button type="button" className="event-aesthetic-btn" onClick={() => setShowAesthetic(true)}>
                  <AestheticSwatch id={form.aesthetic || 'none'} />
                  <span className="event-aesthetic-btn-name">{(EVENT_AESTHETICS.find(a => a.id === (form.aesthetic || 'none')) || {}).name || 'None'}</span>
                  <svg className="event-aesthetic-btn-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>
              {showAesthetic && (
                <EventAestheticPicker
                  current={form.aesthetic}
                  draftEvent={{
                    title: form.title || 'Your event',
                    tags: form.tags,
                    address: form.address || form.locationInput || '',
                    eventDate: form.eventDate ? localInputToISO(form.eventDate) : null,
                    bannerImageUrl: imagePreview || null,
                    isPublic: visibility !== 'private',
                    attendeeCount: 0, interestedCount: 0,
                  }}
                  onSelect={(id) => set('aesthetic', id)}
                  onClose={() => setShowAesthetic(false)}
                />
              )}
              <div className="event-form-row">
                <label className="event-form-label">Title</label>
                <input className="event-form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Event name" required />
              </div>
              <div className="event-form-row">
                <label className="event-form-label">Tags <span className="event-form-hint">(select one or more)</span></label>
                <div className="event-type-pills">
                  {EVENT_TYPES.map(t => {
                    const active = form.tags.includes(t);
                    return (
                      <button key={t} type="button"
                        className={`event-type-pill${active ? ' active' : ''}`}
                        style={active ? { borderColor: categoryColor[t], color: categoryColor[t] } : {}}
                        onClick={() => toggleTag(t)}>{t}</button>
                    );
                  })}
                </div>
              </div>
              <div className="event-form-row">
                <label className="event-form-label">Description</label>
                <ExpandingTextarea className="event-form-input event-form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Tell people what to expect…" title="Description" rows={3} />
              </div>
              <div className="event-form-row">
                <label className="event-form-label">When</label>
                <div className="event-time-presets">
                  {buildDatePresets().map(p => (
                    <button
                      key={p.label}
                      type="button"
                      className="event-time-preset"
                      onClick={() => setForm(f => ({ ...f, eventDate: p.value, endDate: addHoursToInput(p.value, 2) }))}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="event-form-row">
                <label className="event-form-label">Starts</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="event-form-input"
                    type="date"
                    style={{ flex: '1.5' }}
                    value={form.eventDate?.slice(0, 10) ?? ''}
                    onChange={e => {
                      const next = `${e.target.value}T${form.eventDate?.slice(11, 16) || '12:00'}`;
                      setForm(f => {
                        const auto = addHoursToInput(next, 2);
                        const shouldBump = !f.endDate || (next && f.endDate <= next);
                        return { ...f, eventDate: next, endDate: shouldBump ? auto : f.endDate };
                      });
                    }}
                  />
                  <TimeSelect
                    style={{ flex: '1' }}
                    value={form.eventDate?.slice(11, 16) ?? ''}
                    onChange={t => {
                      const datePart = form.eventDate?.slice(0, 10) || '';
                      const next = datePart ? `${datePart}T${t}` : '';
                      setForm(f => {
                        const auto = addHoursToInput(next, 2);
                        const shouldBump = !f.endDate || (next && f.endDate <= next);
                        return { ...f, eventDate: next, endDate: shouldBump ? auto : f.endDate };
                      });
                    }}
                  />
                </div>
              </div>
              <div className="event-form-row">
                <label className="event-form-label">
                  Ends
                  {formatDuration(form.eventDate, form.endDate) && (
                    <span className="event-form-label-hint">
                      {' · '}{formatDuration(form.eventDate, form.endDate)}
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="event-form-input"
                    type="date"
                    style={{ flex: '1.5' }}
                    value={form.endDate?.slice(0, 10) ?? ''}
                    min={form.eventDate?.slice(0, 10) || undefined}
                    onChange={e => {
                      const timePart = form.endDate?.slice(11, 16) || '12:00';
                      set('endDate', `${e.target.value}T${timePart}`);
                    }}
                  />
                  <TimeSelect
                    style={{ flex: '1' }}
                    value={form.endDate?.slice(11, 16) ?? ''}
                    onChange={t => {
                      const datePart = form.endDate?.slice(0, 10) || '';
                      set('endDate', datePart ? `${datePart}T${t}` : '');
                    }}
                  />
                </div>
              </div>
              <div className="event-form-row">
                <label className="event-form-label">Max Attendees</label>
                <input className="event-form-input" type="number" min="1" value={form.maxAttendees} onChange={e => set('maxAttendees', e.target.value)} placeholder="Unlimited" />
              </div>

              {visibility === 'private' && (
                <div className="event-form-row">
                  <label className="event-form-label">Invite people<span className="event-form-label-hint">{invitees.length > 0 ? ` · ${invitees.length} invited` : ''}</span></label>
                  <EventInvitePicker selected={invitees} onChange={setInvitees} />
                </div>
              )}

              <div className="event-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setStep(0)}>← Back</button>
                <button type="button" className="btn-primary" disabled={!form.title.trim()} onClick={goNext}>
                  Next: Set Location →
                </button>
              </div>
            </>
          )}

          {/* ── Step 2: Location + optional Route ── */}
          {step === 2 && (
            <>
              <EventLocationStep form={form} setForm={setForm} routeAttached={!!selectedRoute} />

              {/* Attach a saved route instead — mutually exclusive with the inline
                  cruise path above, so it's hidden once an end location is added. */}
              {!cruiseActiveForm(form) && (
                <div className="event-route-attach">
                  <div className="event-route-attach-header">
                    <span className="event-form-label" style={{ marginBottom: 0 }}>Attach a saved route</span>
                    <span className="event-form-label-hint">Optional — reuse a route from the Routes page instead of drawing one above</span>
                  </div>

                  {!selectedRoute && routeMode === 'none' && (
                    <div className="event-route-actions" style={{ marginTop: 8 }}>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setRouteMode('search')}>
                        Search existing routes
                      </button>
                    </div>
                  )}

                  {!selectedRoute && routeMode === 'search' && (
                    <div className="event-route-search" style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                        <input
                          ref={routeSearchRef}
                          className="event-form-input"
                          style={{ flex: 1 }}
                          value={routeSearch}
                          onChange={e => setRouteSearch(e.target.value)}
                          placeholder="Search routes by name…"
                          autoFocus
                        />
                        <button type="button" className="btn-ghost btn-sm" onClick={() => { setRouteMode('none'); setRouteSearch(''); setRouteResults([]); }}>Cancel</button>
                      </div>
                      {routeSearchLoading && <div className="event-route-loading"><div className="loading-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /></div>}
                      {routeResults.length > 0 && (
                        <div className="event-route-results">
                          {routeResults.map(r => (
                            <button key={r.id} type="button" className="event-route-result-item" onClick={() => { setSelectedRoute(r); setRouteMode('none'); }}>
                              <div className="event-route-result-name">{r.name}</div>
                              <div className="event-route-result-meta">
                                {r.distanceKm && <span>{parseFloat(r.distanceKm).toFixed(1)} km</span>}
                                {r.tags?.length > 0 && <span>{r.tags.slice(0, 3).join(', ')}</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {!routeSearchLoading && routeSearch && routeResults.length === 0 && (
                        <p className="event-route-no-results">No routes found.</p>
                      )}
                    </div>
                  )}

                  {selectedRoute && (
                    <div className="event-route-selected" style={{ marginTop: 8 }}>
                      <div className="event-route-selected-card">
                        <div className="event-route-selected-info">
                          <div className="event-route-selected-name">{selectedRoute.name}</div>
                          <div className="event-route-selected-meta">
                            {selectedRoute.distanceKm && <span>{parseFloat(selectedRoute.distanceKm).toFixed(1)} km</span>}
                            {selectedRoute.tags?.length > 0 && <span>{selectedRoute.tags.slice(0, 3).join(' · ')}</span>}
                          </div>
                        </div>
                        <button type="button" className="btn-ghost btn-sm" onClick={() => { setSelectedRoute(null); setRouteMode('none'); }}>Remove</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="event-modal-actions" style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0, marginTop: 0 }}>
                <button type="button" className="btn-secondary" onClick={() => setStep(1)}>← Back</button>
                <button type="submit" className="btn-primary" disabled={submitting || !form.title.trim()}>{submitting ? 'Creating…' : 'Create Event'}</button>
              </div>
            </>
          )}

        </form>
      </div>
    </div>
    </>
  );
};

// ─── Image Upload Field ───────────────────────────────────────────────────────
const ImageUploadField = ({ preview, onChange }) => {
  const inputRef = useRef();
  return (
    <div
      className="event-image-upload"
      onClick={() => inputRef.current.click()}
      style={preview ? { backgroundImage: `url(${preview})` } : {}}
    >
      {!preview && (
        <div className="event-image-upload-placeholder">
          <span style={{ fontSize: '1.5rem', opacity: 0.4 }}>+</span>
          <span style={{ fontSize: '0.75rem', color: '#8892b0' }}>Add photo</span>
          <span style={{ fontSize: '0.68rem', color: '#5a657a', marginTop: 4, textAlign: 'center', lineHeight: 1.4 }}>You can add more photos once you have created the event</span>
        </div>
      )}
      {preview && <div className="event-image-upload-overlay">Change photo</div>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onChange} />
    </div>
  );
};

// ─── RSVP Buttons ────────────────────────────────────────────────────────────
const RsvpButtons = ({ myStatus, onRsvp, compact = false }) => (
  <div className={`event-rsvp-group${compact ? ' compact' : ''}`} onClick={e => e.stopPropagation()}>
    <button
      className={`event-rsvp-btn going${myStatus === 'attending' ? ' active' : ''}`}
      onClick={() => onRsvp('attending')}
    >
      {myStatus === 'attending' ? 'Going ✓' : 'Going'}
    </button>
    <button
      className={`event-rsvp-btn interested${myStatus === 'interested' ? ' active' : ''}`}
      onClick={() => onRsvp('interested')}
    >
      {myStatus === 'interested' ? 'Interested ✓' : 'Interested'}
    </button>
  </div>
);

// ─── Event Card ───────────────────────────────────────────────────────────────
const EventCard = ({ event, onRsvp, onClick, currentUserId }) => {
  // Themed events render the aesthetic card (same size, decorative frame + tint).
  if (getEventAesthetic(event.aesthetic)) return <AestheticEventCard event={event} onClick={onClick} />;
  const color = primaryTagColor(event);
  const tags = eventTags(event);
  const location = event.address || event.suburb || null;
  const hasImage = !!event.bannerImageUrl;

  return (
    <div className={`event-card${hasImage ? ' event-card--with-image' : ''}`} onClick={onClick} style={{ cursor: 'pointer' }}>
      {hasImage && (
        <div className="event-card-image" style={{ backgroundImage: `url(${eventBannerThumb(event)})` }} />
      )}
      {!hasImage && <div className="event-card-accent" style={{ background: color }} />}
      <div className="event-card-body">
        <div className="event-card-top">
          <div className="event-card-meta">
            {(tags.length ? tags : ['Event']).slice(0, 3).map(t => {
              const c = categoryColor[t] || categoryColor['Other'];
              return <span key={t} className="event-category-badge" style={{ color: c, borderColor: c }}>{t}</span>;
            })}
            {tags.length > 3 && (
              <span className="event-category-badge" style={{ color: categoryColor['Other'], borderColor: categoryColor['Other'] }}>+{tags.length - 3}</span>
            )}
            {!event.isPublic && (
              <span className="event-private-badge">{event.organizerId === currentUserId ? 'Private' : 'Invited'}</span>
            )}
            {event.organizer && (
              <span className="event-card-organizer">by <Link to={`/profile/${event.organizer.username}`} style={{ textDecoration: 'underline', color: 'inherit' }}>{event.organizer.username}</Link></span>
            )}
          </div>
          <RsvpButtons myStatus={event.myStatus} onRsvp={(status) => onRsvp(event, status)} compact />
        </div>

        <h3 className="event-card-title">{event.title}</h3>

        {event.description && (
          <p className="event-card-desc">{event.description}</p>
        )}

        <div className="event-card-details">
          <span className="event-card-detail">
            <CalIcon />
            {formatDate(event.eventDate)}{event.eventDate ? ` · ${formatTime(event.eventDate)}` : ''}
          </span>
          {location && (
            <span className="event-card-detail">
              <PinIcon />
              {location}
            </span>
          )}
          <span className="event-card-detail">
            <PeopleIcon />
            {event.attendeeCount ?? 0} going
            {event.interestedCount > 0 ? ` · ${event.interestedCount} interested` : ''}
            {event.maxAttendees ? ` · ${event.maxAttendees} max` : ''}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Car Pick Modal (shown after Going RSVP) ─────────────────────────────────
const CarPickModal = ({ eventId, userId, onAdded, onSkip }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // userVehicleId being added

  useEffect(() => {
    api.get(`/garages/${userId}/vehicles`)
      .then(({ data }) => {
        const v = data.vehicles || [];
        setVehicles(v);
        // If user has no cars, skip immediately — don't show the modal
        if (v.length === 0) onSkip();
      })
      .catch(() => onSkip())
      .finally(() => setLoading(false));
  }, [userId]);

  const handlePick = async (uv) => {
    setAdding(uv.id);
    try {
      const { data } = await api.post(`/events/${eventId}/vehicles`, { userVehicleId: uv.id });
      onAdded(data.eventVehicle, uv);
    } catch {
      toast.error('Failed to add vehicle');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="car-pick-overlay" onClick={onSkip}>
      <div className="car-pick-modal" onClick={e => e.stopPropagation()}>
        <div className="car-pick-header">
          <h3 className="car-pick-title">Are you bringing a car?</h3>
          <p className="car-pick-subtitle">Select one from your collection to showcase at this event.</p>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}><div className="loading-spinner" /></div>
        ) : vehicles.length === 0 ? (
          <p style={{ color: '#5a657a', fontSize: '0.85rem', textAlign: 'center', padding: '24px 0' }}>No vehicles in your garage yet.</p>
        ) : (
          <div className="car-pick-grid">
            {vehicles.map(uv => {
              const v = uv.vehicle || {};
              const img = uv.images?.[0]?.imageUrl;
              return (
                <button
                  key={uv.id}
                  className="car-pick-card"
                  onClick={() => handlePick(uv)}
                  disabled={!!adding}
                >
                  <div className="car-pick-image" style={img ? { backgroundImage: `url(${img})` } : {}}>
                    {!img && <span style={{ color: '#5a657a', fontSize: '0.75rem' }}>No photo</span>}
                  </div>
                  <div className="car-pick-info">
                    <div className="car-pick-name">{v.year} {v.make} {v.model}</div>
                    {uv.nickname && <div className="car-pick-nickname">{uv.nickname}</div>}
                  </div>
                  {adding === uv.id && <div className="car-pick-adding"><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /></div>}
                </button>
              );
            })}
          </div>
        )}
        <button className="car-pick-skip" onClick={onSkip}>Skip for now</button>
      </div>
    </div>
  );
};

// ─── Event Detail / Edit Modal ────────────────────────────────────────────────

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const EventDetailModal = ({ event: initialEvent, currentUserId, onClose, onRsvp, onUpdate, onDelete }) => {
  const [event, setEvent] = useState(initialEvent);
  const isOwner = event.organizerId === currentUserId;
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('description');

  // Comments
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [annLoading, setAnnLoading] = useState(false);
  const [annText, setAnnText] = useState('');
  const [annPinned, setAnnPinned] = useState(false);
  const [annNotify, setAnnNotify] = useState(true);
  const [annSubmitting, setAnnSubmitting] = useState(false);
  const [showAnnForm, setShowAnnForm] = useState(false);
  const [showAllAnn, setShowAllAnn] = useState(false);

  // Guest list (private events only)
  const [guests, setGuests] = useState([]);
  const [guestsLoading, setGuestsLoading] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const galleryInputRef = useRef(null);

  // Cars attending
  const [carsAttending, setCarsAttending] = useState(event.eventVehicles || []);
  const [carsLoading, setCarsLoading] = useState(false);
  const [carsLoaded, setCarsLoaded] = useState(!!(event.eventVehicles));
  const [showCarPick, setShowCarPick] = useState(false);
  const [calDropdownOpen, setCalDropdownOpen] = useState(false);
  const [form, setForm] = useState({
    title: event.title || '',
    tags: eventTags(event),
    description: event.description || '',
    locationInput: event.address || event.suburb || '',
    address: event.address || event.suburb || '',
    latitude: event.latitude || null,
    longitude: event.longitude || null,
    // Seed the inline cruise path from the saved event.cruisePath (start = the
    // event location above).
    cruiseOpen: !!(event.cruisePath && event.cruisePath.endLatitude != null),
    endInput: event.cruisePath?.endAddress || '',
    endAddress: event.cruisePath?.endAddress || '',
    endLatitude: event.cruisePath?.endLatitude ?? null,
    endLongitude: event.cruisePath?.endLongitude ?? null,
    stops: (event.cruisePath?.waypoints || []).map((w, i) => ({ id: Date.now() + i, input: w.address || '', address: w.address || '', latitude: w.latitude, longitude: w.longitude })),
    cruiseGeometry: event.cruisePath?.routeGeometry || null,
    cruiseDistanceKm: event.cruisePath?.distanceKm ? String(parseFloat(event.cruisePath.distanceKm)) : '',
    eventDate: isoToLocalInput(event.eventDate),
    endDate: isoToLocalInput(event.endDate),
    maxAttendees: event.maxAttendees || '',
    aesthetic: event.aesthetic || null,
  });
  const [imageFile, setImageFile] = useState(null);
  const [showAesthetic, setShowAesthetic] = useState(false);
  const [imagePreview, setImagePreview] = useState(event.bannerImageUrl || null);
  const [submitting, setSubmitting] = useState(false);
  const [editStep, setEditStep] = useState(1); // 1 = details, 2 = location (+ optional route)
  const [editSelectedRoute, setEditSelectedRoute] = useState(event.route || null);
  const [editInvitees, setEditInvitees] = useState([]); // newly-added invitees (private events)
  const [editRouteMode, setEditRouteMode] = useState('none');
  const [editRouteSearch, setEditRouteSearch] = useState('');
  const [editRouteResults, setEditRouteResults] = useState([]);
  const [editRouteSearchLoading, setEditRouteSearchLoading] = useState(false);
  const [notifyPrompt, setNotifyPrompt] = useState(null); // change summary awaiting a notify choice
  const location = event.address || event.suburb || null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTag = (t) => setForm(f => ({
    ...f,
    tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t],
  }));

  useEffect(() => {
    if (editRouteMode !== 'search') return;
    const t = setTimeout(async () => {
      setEditRouteSearchLoading(true);
      try {
        const { data } = await api.get('/routes', { params: { search: editRouteSearch.trim(), includeMine: true, limit: 20 } });
        setEditRouteResults(data.routes || []);
      } catch { setEditRouteResults([]); }
      finally { setEditRouteSearchLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [editRouteSearch, editRouteMode]);

  useEffect(() => {
    if (activeTab !== 'gallery') return;
    setGalleryLoading(true);
    api.get(`/events/${event.id}/photos`)
      .then(({ data }) => setGalleryPhotos(data.photos))
      .catch(() => {})
      .finally(() => setGalleryLoading(false));
  }, [activeTab, event.id]);

  useEffect(() => {
    if (activeTab !== 'discussion') return;
    setCommentsLoading(true);
    api.get(`/events/${event.id}/comments`)
      .then(({ data }) => setComments(data.comments))
      .catch(() => {})
      .finally(() => setCommentsLoading(false));
  }, [activeTab, event.id]);

  useEffect(() => {
    // Load announcements when detail opens (shown in Details tab)
    setAnnLoading(true);
    api.get(`/events/${event.id}/announcements`)
      .then(({ data }) => setAnnouncements(data.announcements))
      .catch(() => {})
      .finally(() => setAnnLoading(false));
  }, [event.id]);

  useEffect(() => {
    // Pull the full event on open — list/create payloads may omit the attached
    // route association, so merge the complete record in.
    api.get(`/events/${event.id}`)
      .then(({ data }) => { if (data.event) setEvent(prev => ({ ...prev, ...data.event })); })
      .catch(() => {});
  }, [event.id]);

  useEffect(() => {
    // Guest list — private events only (server returns [] for public).
    if (event.isPublic) { setGuests([]); return; }
    setGuestsLoading(true);
    api.get(`/events/${event.id}/invites`)
      .then(({ data }) => setGuests(data.invites || []))
      .catch(() => {})
      .finally(() => setGuestsLoading(false));
  }, [event.id, event.isPublic]);

  useEffect(() => {
    if (activeTab !== 'cars' || carsLoaded) return;
    setCarsLoading(true);
    api.get(`/events/${event.id}`)
      .then(({ data }) => { setCarsAttending(data.event?.eventVehicles || []); setCarsLoaded(true); })
      .catch(() => {})
      .finally(() => setCarsLoading(false));
  }, [activeTab, event.id, carsLoaded]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || commentSubmitting) return;
    setCommentSubmitting(true);
    try {
      const { data } = await api.post(`/events/${event.id}/comments`, { body: commentText });
      setComments(cs => [...cs, data.comment]);
      setCommentText('');
    } catch { toast.error('Failed to post comment'); }
    finally { setCommentSubmitting(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/events/${event.id}/comments/${commentId}`);
      setComments(cs => cs.filter(c => c.id !== commentId));
    } catch { toast.error('Failed to delete comment'); }
  };

  const handlePostAnnouncement = async (e) => {
    e.preventDefault();
    if (!annText.trim() || annSubmitting) return;
    setAnnSubmitting(true);
    try {
      const { data } = await api.post(`/events/${event.id}/announcements`, { body: annText, isPinned: annPinned, notify: annNotify });
      setAnnouncements(prev => [data.announcement, ...prev]);
      setAnnText('');
      setAnnPinned(false);
      setAnnNotify(true);
      setShowAnnForm(false);
      toast.success(annNotify ? 'Update posted' : 'Update posted silently');
    } catch { toast.error('Failed to post announcement'); }
    finally { setAnnSubmitting(false); }
  };

  const handleDeleteAnnouncement = async (annId) => {
    try {
      await api.delete(`/events/${event.id}/announcements/${annId}`);
      setAnnouncements(prev => prev.filter(a => a.id !== annId));
    } catch { toast.error('Failed to delete announcement'); }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setGalleryUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('photos', f));
      const { data } = await api.post(`/events/${event.id}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setGalleryPhotos(prev => [...data.photos, ...prev]);
      toast.success(`${data.photos.length} photo${data.photos.length !== 1 ? 's' : ''} added`);
    } catch {
      toast.error('Failed to upload photos');
    } finally {
      setGalleryUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteGalleryPhoto = async (photoId) => {
    try {
      await api.delete(`/events/${event.id}/photos/${photoId}`);
      setGalleryPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch {
      toast.error('Failed to delete photo');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const goEditNext = () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setEditStep(2);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditStep(1);
    setImageFile(null);
    setImagePreview(event.bannerImageUrl || null);
  };

  // Did a material fact (time or location) change vs the saved event?
  const editMaterialChanged = (
    form.eventDate !== isoToLocalInput(event.eventDate) ||
    form.endDate !== isoToLocalInput(event.endDate) ||
    (form.address || '') !== (event.address || '') ||
    String(form.latitude ?? '') !== String(event.latitude ?? '') ||
    String(form.longitude ?? '') !== String(event.longitude ?? '')
  );

  // Auto-drafted "what changed" message for the notify prompt.
  const buildChangeMessage = () => {
    const parts = [];
    if (form.eventDate !== isoToLocalInput(event.eventDate)) {
      const iso = localInputToISO(form.eventDate);
      parts.push(iso ? `Start time moved to ${formatDate(iso)} · ${formatTime(iso)}` : 'Start time updated');
    }
    if (form.endDate !== isoToLocalInput(event.endDate)) {
      const iso = localInputToISO(form.endDate);
      parts.push(iso ? `End time moved to ${formatTime(iso)}` : 'End time updated');
    }
    const locChanged = (form.address || '') !== (event.address || '') ||
      String(form.latitude ?? '') !== String(event.latitude ?? '') ||
      String(form.longitude ?? '') !== String(event.longitude ?? '');
    if (locChanged) {
      const where = form.address || form.locationInput;
      parts.push(where ? `Location changed to ${where}` : 'Location updated');
    }
    return parts.join('. ');
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    try {
      await api.delete(`/events/${event.id}`);
      toast.success('Event deleted');
      onDelete(event.id);
      onClose();
    } catch { toast.error('Failed to delete event'); }
  };

  // Commit the edit. `notify` posts the change as an update (firing the push).
  const doSave = async (notify) => {
    setSubmitting(true);
    try {
      const { data } = await api.put(`/events/${event.id}`, {
        title: form.title, tags: form.tags, description: form.description,
        address: form.address || form.locationInput || null,
        latitude: form.latitude, longitude: form.longitude,
        eventDate: localInputToISO(form.eventDate),
        endDate: localInputToISO(form.endDate),
        maxAttendees: form.maxAttendees ? parseInt(form.maxAttendees) : null,
        aesthetic: form.aesthetic || null,
        // Attach a saved route OR carry an inline cruise path — never both.
        routeId: editSelectedRoute?.id || null,
        cruisePath: editSelectedRoute ? null : buildCruisePayload(form),
        invites: (!event.isPublic && editInvitees.length) ? editInvitees.map(u => u.id) : undefined,
      });
      let updated = data.event;
      if (imageFile) {
        const fd = new FormData();
        fd.append('banner', imageFile);
        const { data: bannerData } = await api.post(`/events/${event.id}/banner`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        updated = { ...updated, bannerImageUrl: bannerData.bannerImageUrl };
      }
      const withRoute = { ...updated, route: editSelectedRoute || null };
      setEvent(e => ({ ...e, ...withRoute }));
      onUpdate({ ...event, ...withRoute });

      if (notify && notifyPrompt) {
        try {
          const { data: annData } = await api.post(`/events/${event.id}/announcements`, {
            body: notifyPrompt, isPinned: false, notify: true,
          });
          setAnnouncements(prev => [annData.announcement, ...prev]);
          toast.success('Event updated · attendees notified');
        } catch { toast.success('Event updated!'); }
      } else {
        toast.success('Event updated!');
      }

      // Refresh the guest list if invitees were added to a private event.
      if (!event.isPublic && editInvitees.length) {
        api.get(`/events/${event.id}/invites`).then(({ data }) => setGuests(data.invites || [])).catch(() => {});
      }

      setEditing(false);
      setEditStep(1);
      setImageFile(null);
      setEditInvitees([]);
      setNotifyPrompt(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update event');
    } finally { setSubmitting(false); }
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Title is required');
    // Pressing Enter on step 1 advances rather than submitting.
    if (editStep !== 2) { setEditStep(2); return; }
    // Time or location changed — ask whether to notify before committing.
    if (editMaterialChanged) { setNotifyPrompt(buildChangeMessage()); return; }
    doSave(false);
  };

  // ── Edit mode: mirrors the Create Event modal exactly (2 steps, location on step 2) ──
  if (editing) {
    const isMapStep = editStep === 2;
    return (
      <div className="event-modal-overlay">
        <div className={`event-modal${isMapStep ? ' event-modal--map' : ''}`}>
          <div
            className="event-modal-header"
            style={isMapStep
              ? { padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }
              : undefined}
          >
            <h2 className="event-modal-title">{editStep === 1 ? 'Edit Event' : 'Set Location'}</h2>
            <button className="event-modal-close" onClick={cancelEdit}>✕</button>
          </div>

          <form
            onSubmit={handleSave}
            className="event-modal-form"
            style={isMapStep
              ? { flex: 1, minHeight: 0, padding: 0, gap: 0 }
              : undefined}
          >

            {/* ── Step 1: Details ── */}
            {editStep === 1 && (
              <>
                <div className="event-form-row">
                  <label className="event-form-label">Photo</label>
                  <ImageUploadField preview={imagePreview} onChange={handleImageChange} />
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Event Aesthetic</label>
                  <button type="button" className="event-aesthetic-btn" onClick={() => setShowAesthetic(true)}>
                    <AestheticSwatch id={form.aesthetic || 'none'} />
                    <span className="event-aesthetic-btn-name">{(EVENT_AESTHETICS.find(a => a.id === (form.aesthetic || 'none')) || {}).name || 'None'}</span>
                    <svg className="event-aesthetic-btn-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                </div>
                {showAesthetic && (
                  <EventAestheticPicker
                    current={form.aesthetic}
                    draftEvent={{
                      ...event,
                      title: form.title || event.title,
                      tags: form.tags,
                      address: form.address || form.locationInput || event.address,
                      eventDate: form.eventDate ? localInputToISO(form.eventDate) : event.eventDate,
                      bannerImageUrl: imagePreview || event.bannerImageUrl || null,
                    }}
                    onSelect={(id) => set('aesthetic', id)}
                    onClose={() => setShowAesthetic(false)}
                  />
                )}
                <div className="event-form-row">
                  <label className="event-form-label">Title</label>
                  <input className="event-form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Event name" required />
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Tags <span className="event-form-hint">(select one or more)</span></label>
                  <div className="event-type-pills">
                    {EVENT_TYPES.map(t => {
                      const active = form.tags.includes(t);
                      return (
                        <button key={t} type="button"
                          className={`event-type-pill${active ? ' active' : ''}`}
                          style={active ? { borderColor: categoryColor[t], color: categoryColor[t] } : {}}
                          onClick={() => toggleTag(t)}>{t}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Description</label>
                  <ExpandingTextarea className="event-form-input event-form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Tell people what to expect…" title="Description" rows={3} />
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">When</label>
                  <div className="event-time-presets">
                    {buildDatePresets().map(p => (
                      <button
                        key={p.label}
                        type="button"
                        className="event-time-preset"
                        onClick={() => setForm(f => ({ ...f, eventDate: p.value, endDate: addHoursToInput(p.value, 2) }))}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Starts</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="event-form-input"
                      type="date"
                      style={{ flex: '1.5' }}
                      value={form.eventDate?.slice(0, 10) ?? ''}
                      onChange={e => {
                        const next = `${e.target.value}T${form.eventDate?.slice(11, 16) || '12:00'}`;
                        setForm(f => {
                          const auto = addHoursToInput(next, 2);
                          const shouldBump = !f.endDate || (next && f.endDate <= next);
                          return { ...f, eventDate: next, endDate: shouldBump ? auto : f.endDate };
                        });
                      }}
                    />
                    <TimeSelect
                      style={{ flex: '1' }}
                      value={form.eventDate?.slice(11, 16) ?? ''}
                      onChange={t => {
                        const datePart = form.eventDate?.slice(0, 10) || '';
                        const next = datePart ? `${datePart}T${t}` : '';
                        setForm(f => {
                          const auto = addHoursToInput(next, 2);
                          const shouldBump = !f.endDate || (next && f.endDate <= next);
                          return { ...f, eventDate: next, endDate: shouldBump ? auto : f.endDate };
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">
                    Ends
                    {formatDuration(form.eventDate, form.endDate) && (
                      <span className="event-form-label-hint">
                        {' · '}{formatDuration(form.eventDate, form.endDate)}
                      </span>
                    )}
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="event-form-input"
                      type="date"
                      style={{ flex: '1.5' }}
                      value={form.endDate?.slice(0, 10) ?? ''}
                      min={form.eventDate?.slice(0, 10) || undefined}
                      onChange={e => {
                        const timePart = form.endDate?.slice(11, 16) || '12:00';
                        set('endDate', `${e.target.value}T${timePart}`);
                      }}
                    />
                    <TimeSelect
                      style={{ flex: '1' }}
                      value={form.endDate?.slice(11, 16) ?? ''}
                      onChange={t => {
                        const datePart = form.endDate?.slice(0, 10) || '';
                        set('endDate', datePart ? `${datePart}T${t}` : '');
                      }}
                    />
                  </div>
                </div>
                <div className="event-form-row">
                  <label className="event-form-label">Max Attendees</label>
                  <input className="event-form-input" type="number" min="1" value={form.maxAttendees} onChange={e => set('maxAttendees', e.target.value)} placeholder="Unlimited" />
                </div>

                {!event.isPublic && (
                  <div className="event-form-row">
                    <label className="event-form-label">Invite more people<span className="event-form-label-hint">{editInvitees.length > 0 ? ` · ${editInvitees.length} to add` : ''}</span></label>
                    <EventInvitePicker selected={editInvitees} onChange={setEditInvitees} />
                  </div>
                )}

                <div className="event-modal-actions" style={{ justifyContent: 'space-between' }}>
                  <button type="button" className="btn-danger" disabled={submitting} onClick={handleDeleteEvent}>Delete</button>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" className="btn-secondary" onClick={cancelEdit}>Cancel</button>
                    <button type="button" className="btn-primary" disabled={!form.title.trim()} onClick={goEditNext}>
                      Next: Set Location →
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── Step 2: Location + optional Route ── */}
            {editStep === 2 && (
              <>
                <EventLocationStep form={form} setForm={setForm} routeAttached={!!editSelectedRoute} />

                {/* Attach a saved route instead — hidden while an inline cruise
                    path is being built (the two are mutually exclusive). */}
                {!cruiseActiveForm(form) && (
                  <div className="event-route-attach">
                    <div className="event-route-attach-header">
                      <span className="event-form-label" style={{ marginBottom: 0 }}>Attach a saved route</span>
                      <span className="event-form-label-hint">Optional — reuse a route from the Routes page instead of drawing one above</span>
                    </div>

                    {!editSelectedRoute && editRouteMode === 'none' && (
                      <div className="event-route-actions" style={{ marginTop: 8 }}>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => setEditRouteMode('search')}>
                          Search existing routes
                        </button>
                      </div>
                    )}

                    {!editSelectedRoute && editRouteMode === 'search' && (
                      <div className="event-route-search" style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                          <input
                            className="event-form-input"
                            style={{ flex: 1 }}
                            value={editRouteSearch}
                            onChange={e => setEditRouteSearch(e.target.value)}
                            placeholder="Search routes by name…"
                            autoFocus
                          />
                          <button type="button" className="btn-ghost btn-sm" onClick={() => { setEditRouteMode('none'); setEditRouteSearch(''); setEditRouteResults([]); }}>Cancel</button>
                        </div>
                        {editRouteSearchLoading && <div className="event-route-loading"><div className="loading-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /></div>}
                        {editRouteResults.length > 0 && (
                          <div className="event-route-results">
                            {editRouteResults.map(r => (
                              <button key={r.id} type="button" className="event-route-result-item" onClick={() => { setEditSelectedRoute(r); setEditRouteMode('none'); }}>
                                <div className="event-route-result-name">{r.name}</div>
                                <div className="event-route-result-meta">
                                  {r.distanceKm && <span>{parseFloat(r.distanceKm).toFixed(1)} km</span>}
                                  {r.tags?.length > 0 && <span>{r.tags.slice(0, 3).join(', ')}</span>}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        {!editRouteSearchLoading && editRouteSearch && editRouteResults.length === 0 && (
                          <p className="event-route-no-results">No routes found.</p>
                        )}
                      </div>
                    )}

                    {editSelectedRoute && (
                      <div className="event-route-selected" style={{ marginTop: 8 }}>
                        <div className="event-route-selected-card">
                          <div className="event-route-selected-info">
                            <div className="event-route-selected-name">{editSelectedRoute.name}</div>
                            <div className="event-route-selected-meta">
                              {editSelectedRoute.distanceKm && <span>{parseFloat(editSelectedRoute.distanceKm).toFixed(1)} km</span>}
                              {editSelectedRoute.tags?.length > 0 && <span>{editSelectedRoute.tags.slice(0, 3).join(' · ')}</span>}
                            </div>
                          </div>
                          <button type="button" className="btn-ghost btn-sm" onClick={() => { setEditSelectedRoute(null); setEditRouteMode('none'); }}>Remove</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="event-modal-actions" style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0, marginTop: 0 }}>
                  <button type="button" className="btn-secondary" onClick={() => setEditStep(1)}>← Back</button>
                  <button type="submit" className="btn-primary" disabled={submitting || !form.title.trim()}>{submitting ? 'Saving…' : 'Save Changes'}</button>
                </div>
              </>
            )}

          </form>
        </div>

        {/* Notify-on-change prompt — appears when Save is pressed after a time/location change */}
        {notifyPrompt && (
          <div className="event-detail-overlay" style={{ zIndex: 3000 }}>
            <div className="notify-confirm" onClick={e => e.stopPropagation()}>
              <h3 className="notify-confirm-title">Notify attendees of this change?</h3>
              <p className="notify-confirm-msg">{notifyPrompt}</p>
              <p className="notify-confirm-sub">Everyone going or interested will get a push notification.</p>
              <div className="notify-confirm-actions">
                <button type="button" className="btn-ghost" disabled={submitting} onClick={() => setNotifyPrompt(null)}>Cancel</button>
                <button type="button" className="btn-secondary" disabled={submitting} onClick={() => doSave(false)}>Don't notify</button>
                <button type="button" className="btn-primary" disabled={submitting} onClick={() => doSave(true)}>{submitting ? 'Saving…' : 'Notify'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="event-detail-overlay" onClick={onClose}>
      <div className="event-detail-popup-wrap">
        <button className="event-detail-close-float" onClick={onClose}>✕</button>
        <div className="event-detail-popup" onClick={e => e.stopPropagation()}>

          <>
            {/* Hero banner — themed events get the aesthetic banner (frame + tint) */}
            {getEventAesthetic(event.aesthetic) ? (
              <AestheticDetailBanner event={event} />
            ) : (event.bannerImageUrl) && (
              <div className="route-detail-map-top" style={{ height: 280 }}>
                <img src={event.bannerImageUrl} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            )}

            {/* Header */}
            <div className="route-detail-header">
              <div className="route-detail-header-left">
                <h2 className="route-detail-title">{event.title}</h2>
                <div className="route-detail-meta">
                  {event.organizer && <span className="route-detail-creator">by <Link to={`/profile/${event.organizer.username}`} style={{ textDecoration: 'underline', color: 'inherit' }}>{event.organizer.username}</Link></span>}
                  {(eventTags(event).length ? eventTags(event) : ['Event']).map(t => {
                    const c = categoryColor[t] || categoryColor['Other'];
                    return <span key={t} className="event-category-badge" style={{ color: c, borderColor: c, fontSize: '0.75rem', padding: '2px 8px' }}>{t}</span>;
                  })}
                </div>
              </div>
              <div className="route-detail-header-right">
                <RsvpButtons
                  myStatus={event.myStatus}
                  onRsvp={async (status) => {
                    const wasAttending = event.myStatus === 'attending';
                    const removing = event.myStatus === status;
                    const leavingGoing = wasAttending && (removing || status !== 'attending');

                    onRsvp(event, status);
                    setEvent(prev => {
                      const wasGoing = prev.myStatus === 'attending';
                      const wasInterested = prev.myStatus === 'interested';
                      const rem = prev.myStatus === status;
                      return {
                        ...prev,
                        myStatus: rem ? null : status,
                        attendeeCount: (prev.attendeeCount || 0)
                          + (status === 'attending' && !rem ? 1 : 0)
                          + (wasGoing && (rem || status === 'interested') ? -1 : 0),
                        interestedCount: (prev.interestedCount || 0)
                          + (status === 'interested' && !rem ? 1 : 0)
                          + (wasInterested && (rem || status === 'attending') ? -1 : 0),
                      };
                    });

                    // Update local cars state — server already removed their vehicle
                    if (leavingGoing) {
                      setCarsAttending(prev => prev.filter(c => String(c.userId) !== String(currentUserId)));
                      // Force reload next time Cars tab is opened
                      if (carsLoaded) setCarsLoaded(false);
                    }

                    // Show car picker if user just RSVPd as going
                    if (status === 'attending' && !removing && !wasAttending) {
                      setShowCarPick(true);
                    }
                  }}
                />
                <button
                  className="event-share-btn"
                  title="Share event"
                  onClick={() => {
                    const url = `${window.location.origin}/events?event=${event.id}`;
                    navigator.clipboard.writeText(url).then(() => toast.success('Link copied to clipboard'));
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                  </svg>
                  Share
                </button>
                {isOwner && <button className="event-edit-btn" onClick={() => { setEditStep(1); setEditSelectedRoute(event.route || null); setEditRouteMode('none'); setEditInvitees([]); setEditing(true); }}>Edit</button>}
                {!isOwner && event.organizerId && (
                  <ReportBlockMenu
                    targetType="event"
                    targetId={event.id}
                    targetUserId={event.organizerId}
                    targetLabel="event"
                    onReported={() => { onDelete?.(event.id); onClose(); }}
                    onBlocked={() => { onDelete?.(event.id); onClose(); }}
                  />
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="route-detail-tabs">
              {[
                { key: 'description', label: 'Info', Icon: InfoTabIcon },
                { key: 'location', label: 'Location', Icon: LocationTabIcon },
                { key: 'cars', label: `Cars${carsAttending.length > 0 ? ` (${carsAttending.length})` : ''}`, Icon: CarTabIcon },
                { key: 'discussion', label: `Chat${comments.length > 0 ? ` (${comments.length})` : ''}`, Icon: ChatTabIcon },
                { key: 'gallery', label: 'Gallery', Icon: GalleryTabIcon },
                ...(!event.isPublic ? [{ key: 'guests', label: `Guests${guests.length > 0 ? ` (${guests.length + 1})` : ''}`, Icon: PeopleIcon }] : []),
              ].map(tab => (
                <button key={tab.key}
                  className={`route-detail-tab${activeTab === tab.key ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                ><tab.Icon /><span>{tab.label}</span></button>
              ))}
            </div>

            {/* Tab body */}
            <div className="route-detail-body">

              {activeTab === 'description' && (
                <div className="route-tab-details">
                  {/* Announcements */}
                  {(announcements.length > 0 || isOwner) && (
                    <div className="event-announcements">
                      <div className="event-announcements-header">
                        <span className="event-announcements-title">Updates</span>
                        {isOwner && (
                          <button className="event-ann-post-btn" onClick={() => setShowAnnForm(f => !f)}>
                            {showAnnForm ? 'Cancel' : '+ Post Update'}
                          </button>
                        )}
                      </div>

                      {showAnnForm && (
                        <form onSubmit={handlePostAnnouncement} className="event-ann-form">
                          <div className="event-ann-hint">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                            <span>Changing the <strong>time</strong> or <strong>location</strong>? Edit the event instead — attendees are notified of the change automatically. Use updates for everything else.</span>
                          </div>
                          <textarea
                            className="event-form-input"
                            value={annText}
                            onChange={e => setAnnText(e.target.value)}
                            placeholder="e.g. Parking has moved to Gate B. See you all there!"
                            rows={3}
                            style={{ resize: 'vertical', width: '100%' }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#9aa3b2', cursor: 'pointer' }}>
                              <input type="checkbox" checked={annNotify} onChange={e => setAnnNotify(e.target.checked)} />
                              Push notify users
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#9aa3b2', cursor: 'pointer' }}>
                              <input type="checkbox" checked={annPinned} onChange={e => setAnnPinned(e.target.checked)} />
                              Pin this update
                            </label>
                            <button type="submit" className="btn btn-primary btn-sm" disabled={annSubmitting || !annText.trim()} style={{ marginLeft: 'auto' }}>
                              {annSubmitting ? 'Posting…' : 'Post'}
                            </button>
                          </div>
                          {!annNotify && (
                            <p style={{ fontSize: '0.74rem', color: '#5a657a', marginTop: 6 }}>Attendees won't get a notification for this update.</p>
                          )}
                        </form>
                      )}

                      {(() => {
                        const pinned = announcements.filter(a => a.isPinned);
                        const rest = announcements.filter(a => !a.isPinned);
                        const visibleRest = showAllAnn ? rest : rest.slice(0, 1);
                        const hiddenCount = rest.length - visibleRest.length;
                        const renderAnn = (ann) => (
                          <div key={ann.id} className={`event-announcement${ann.isPinned ? ' pinned' : ''}`}>
                            {ann.isPinned && <div className="event-ann-pin-label">Pinned</div>}
                            <p className="event-ann-body">{ann.body}</p>
                            <div className="event-ann-meta">
                              <span>{timeAgo(ann.createdAt)}</span>
                              {isOwner && (
                                <button className="event-ann-delete" onClick={() => handleDeleteAnnouncement(ann.id)}>Delete</button>
                              )}
                            </div>
                          </div>
                        );
                        return (
                          <>
                            {pinned.map(renderAnn)}
                            {visibleRest.map(renderAnn)}
                            {(hiddenCount > 0 || (showAllAnn && rest.length > 1)) && (
                              <button type="button" className="event-ann-showmore" onClick={() => setShowAllAnn(s => !s)}>
                                {showAllAnn ? 'Show less' : `Show ${hiddenCount} more update${hiddenCount !== 1 ? 's' : ''}`}
                              </button>
                            )}
                          </>
                        );
                      })()}

                      {isOwner && announcements.length === 0 && !showAnnForm && (
                        <p style={{ color: '#5a657a', fontSize: '0.82rem' }}>No updates yet. Post one to keep attendees informed.</p>
                      )}
                    </div>
                  )}

                  <div className="event-detail-info" style={{ marginBottom: 16 }}>
                    <div className="event-detail-info-row">
                      <CalIcon />
                      <div>
                        <div>{formatDate(event.eventDate)}{event.eventDate ? ` · ${formatTime(event.eventDate)}` : 'TBC'}</div>
                        {event.endDate && <div style={{ fontSize: '0.8rem', marginTop: 2, opacity: 0.6 }}>Until {formatDate(event.endDate)} · {formatTime(event.endDate)}</div>}
                      </div>
                    </div>
                    <div className="event-detail-info-row">
                      <PeopleIcon />
                      <div>
                        {event.attendeeCount ?? 0} going
                        {event.interestedCount > 0 ? ` · ${event.interestedCount} interested` : ''}
                        {event.maxAttendees ? ` · ${event.maxAttendees} max` : ''}
                      </div>
                    </div>
                    {location && (
                      <div className="event-detail-info-row">
                        <PinIcon />
                        <div>{location}</div>
                      </div>
                    )}
                  </div>
                  {event.description
                    ? <p className="route-detail-desc" style={{ whiteSpace: 'pre-wrap' }}>{renderWithLinks(event.description)}</p>
                    : <p className="route-empty-hint">No description provided.</p>
                  }

                  {/* Add to Calendar */}
                  {event.eventDate && (
                    <div className="event-cal-row" style={{ position: 'relative' }}>
                      <button
                        className="event-cal-btn"
                        onClick={() => setCalDropdownOpen(o => !o)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        Add to Calendar
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                      {calDropdownOpen && (
                        <div className="event-cal-dropdown">
                          <a
                            className="event-cal-option"
                            href={toGCalUrl(event)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setCalDropdownOpen(false)}
                          >
                            Google Calendar
                          </a>
                          <button
                            className="event-cal-option"
                            onClick={() => { downloadICS(event); setCalDropdownOpen(false); }}
                          >
                            Apple / Outlook (.ics)
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'cars' && (
                <div className="route-tab-details">
                  {carsLoading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}><div className="loading-spinner" /></div>
                  ) : (
                    <>
                      {event.myStatus === 'attending' && (
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                          <button className="btn-secondary btn-sm" onClick={() => setShowCarPick(true)}>
                            + Bring a car
                          </button>
                        </div>
                      )}
                      {carsAttending.length === 0 ? (
                        <p className="route-empty-hint">No vehicles added yet. Be the first to showcase yours.</p>
                      ) : (
                        <div className="event-cars-grid">
                          {carsAttending.map(ev => {
                            const uv = ev.userVehicle || {};
                            const v = uv.vehicle || {};
                            const img = uv.images?.[0]?.imageUrl;
                            const isMyVehicle = ev.userId === currentUserId;
                            return (
                              <div key={ev.id} className="event-car-card">
                                <div className="event-car-image" style={img ? { backgroundImage: `url(${img})` } : {}}>
                                  {!img && <span style={{ color: '#5a657a', fontSize: '0.72rem' }}>No photo</span>}
                                </div>
                                <div className="event-car-info">
                                  <div className="event-car-name">{v.year} {v.make} {v.model}</div>
                                  {uv.nickname && <div className="event-car-nickname">{uv.nickname}</div>}
                                  {isMyVehicle && (
                                    <button
                                      className="event-car-remove"
                                      onClick={async () => {
                                        try {
                                          await api.delete(`/events/${event.id}/vehicles/${ev.id}`);
                                          setCarsAttending(prev => prev.filter(c => c.id !== ev.id));
                                        } catch { toast.error('Failed to remove vehicle'); }
                                      }}
                                    >Remove</button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'discussion' && (
                <div className="event-chat">
                  {commentsLoading ? (
                    <div className="event-chat-loading"><div className="loading-spinner" /></div>
                  ) : comments.length === 0 ? (
                    <div className="event-chat-empty">
                      <ChatEmptyIcon />
                      <p>No messages yet</p>
                      <span>Start the conversation about this event.</span>
                    </div>
                  ) : (
                    <div className="event-chat-messages">
                      {comments.map(c => {
                        const cAuthorId = c.author?.id || c.authorId;
                        const isOwnComment = cAuthorId === currentUserId;
                        const canDelete = c.authorId === currentUserId || cAuthorId === currentUserId || isOwner;
                        return (
                          <div key={c.id} className={`event-chat-row${isOwnComment ? ' own' : ''}`}>
                            {!isOwnComment && (
                              <Link to={`/profile/${c.author?.username}`} className="event-chat-avatar">
                                {c.author?.profileImageUrl
                                  ? <img src={c.author.profileImageUrl} alt="" />
                                  : c.author?.username?.[0]?.toUpperCase()}
                              </Link>
                            )}
                            <div className="event-chat-col">
                              {!isOwnComment && (
                                <Link to={`/profile/${c.author?.username}`} className="event-chat-author">{c.author?.username}</Link>
                              )}
                              <div className="event-chat-bubble">
                                <p className="event-chat-text">{c.body}</p>
                              </div>
                              <div className="event-chat-meta">
                                <span className="event-chat-time">{timeAgo(c.createdAt)}</span>
                                {canDelete && (
                                  <button className="event-chat-delete" onClick={() => handleDeleteComment(c.id)}>Delete</button>
                                )}
                                {!isOwnComment && cAuthorId && (
                                  <ReportBlockMenu
                                    targetType="event_comment"
                                    targetId={c.id}
                                    targetUserId={cAuthorId}
                                    targetLabel="comment"
                                    onReported={() => setComments(cs => cs.filter(x => x.id !== c.id))}
                                    onBlocked={(blockedUserId) => setComments(cs => cs.filter(x => (x.author?.id || x.authorId) !== blockedUserId))}
                                    triggerSize="small"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <form onSubmit={handleComment} className="event-chat-composer">
                    <textarea
                      className="event-chat-input"
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(e); } }}
                      placeholder="Write a message…"
                      rows={1}
                    />
                    <button type="submit" className="event-chat-send" disabled={commentSubmitting || !commentText.trim()} aria-label="Send message">
                      <SendIcon />
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'location' && (
                <div className="route-tab-details">
                  {location && (
                    <>
                      {(event.route || event.cruisePath) && <div className="event-loc-heading"><PinIcon /><span>Meet point</span></div>}
                      <div className="event-detail-map" style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
                        {event.latitude && event.longitude ? (
                          <iframe
                            title="event-map"
                            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&q=${parseFloat(event.latitude)},${parseFloat(event.longitude)}&zoom=10`}
                            allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                          />
                        ) : (
                          <div className="event-detail-map-placeholder"><PinIcon /><span>{location}</span></div>
                        )}
                        <a className="event-detail-map-link"
                          href={event.latitude && event.longitude
                            ? `https://www.google.com/maps?q=${event.latitude},${event.longitude}`
                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
                          target="_blank" rel="noopener noreferrer">
                          Open in Google Maps ↗
                        </a>
                      </div>
                      <div className="event-detail-address">{location}</div>
                    </>
                  )}

                  {event.route && (
                    <div style={{ marginTop: location ? 22 : 0 }}>
                      <div className="event-loc-heading"><RouteTabIcon /><span>Route{event.route.name ? ` · ${event.route.name}` : ''}</span></div>
                      <EmbeddedRouteView route={event.route} currentUserId={event.organizerId} />
                    </div>
                  )}

                  {!event.route && event.cruisePath && (
                    <div style={{ marginTop: location ? 22 : 0 }}>
                      <div className="event-loc-heading"><RouteTabIcon /><span>Cruise route</span></div>
                      <EmbeddedCruiseView event={event} />
                    </div>
                  )}

                  {!location && !event.route && !event.cruisePath && <p className="route-empty-hint">No location set for this event.</p>}
                </div>
              )}

              {activeTab === 'gallery' && (
                <div className="route-tab-details">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontSize: '0.8rem', color: '#5a657a' }}>
                      {galleryPhotos.length} {galleryPhotos.length === 1 ? 'photo' : 'photos'}
                    </span>
                    <button
                      className="btn-secondary btn-sm"
                      disabled={galleryUploading}
                      onClick={() => galleryInputRef.current?.click()}
                    >
                      {galleryUploading ? 'Uploading…' : '+ Add Photos'}
                    </button>
                    <input
                      ref={galleryInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleGalleryUpload}
                    />
                  </div>

                  {galleryLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                      <div className="loading-spinner" />
                    </div>
                  ) : galleryPhotos.length === 0 ? (
                    <p className="route-empty-hint">No photos yet. Be the first to add some!</p>
                  ) : (
                    <div className="event-gallery-grid">
                      {galleryPhotos.map(photo => {
                        const uploaderId = photo.uploader?.id || photo.userId;
                        const isOwnPhoto = uploaderId === currentUserId;
                        return (
                          <div key={photo.id} className="event-gallery-item" onClick={() => setLightboxPhoto(photo)} style={{ position: 'relative' }}>
                            <img src={photo.imageUrl} alt="" loading="lazy" decoding="async" />
                            {(photo.userId === currentUserId || isOwner) && (
                              <button
                                className="event-gallery-delete"
                                onClick={e => { e.stopPropagation(); handleDeleteGalleryPhoto(photo.id); }}
                                title="Delete photo"
                              >✕</button>
                            )}
                            {!isOwnPhoto && uploaderId && (
                              <div
                                onClick={e => e.stopPropagation()}
                                style={{ position: 'absolute', top: 6, left: 6, zIndex: 2, background: 'rgba(0,0,0,0.55)', borderRadius: 999 }}
                              >
                                <ReportBlockMenu
                                  targetType="event_photo"
                                  targetId={photo.id}
                                  targetUserId={uploaderId}
                                  targetLabel="photo"
                                  onReported={() => setGalleryPhotos(prev => prev.filter(p => p.id !== photo.id))}
                                  onBlocked={(blockedUserId) => setGalleryPhotos(prev => prev.filter(p => (p.uploader?.id || p.userId) !== blockedUserId))}
                                  triggerSize="small"
                                />
                              </div>
                            )}
                            <div className="event-gallery-uploader">{photo.uploader?.username}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {lightboxPhoto && (
                    <div className="event-lightbox" onClick={() => setLightboxPhoto(null)}>
                      <img src={lightboxPhoto.imageUrl} alt="" loading="lazy" decoding="async" onClick={e => e.stopPropagation()} />
                      <button className="event-lightbox-close" onClick={() => setLightboxPhoto(null)}>✕</button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'guests' && !event.isPublic && (
                <div className="route-tab-details">
                  <p className="event-guests-intro">Private event — only the host and invited guests can see it.</p>
                  <div className="event-guest-row">
                    <div className="event-guest-avatar">
                      {event.organizer?.profileImageUrl ? <img src={event.organizer.profileImageUrl} alt="" /> : (event.organizer?.username?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="event-guest-info">
                      <Link to={`/profile/${event.organizer?.username}`} className="event-guest-name">{event.organizer?.username}</Link>
                      <span className="event-guest-sub">Host</span>
                    </div>
                  </div>
                  {guestsLoading && guests.length === 0 ? (
                    <p className="route-empty-hint">Loading guests…</p>
                  ) : guests.length === 0 ? (
                    <p className="route-empty-hint" style={{ marginTop: 8 }}>No one invited yet.{isOwner ? ' Edit the event to invite people.' : ''}</p>
                  ) : (
                    guests.map(g => (
                      <div key={g.id} className="event-guest-row">
                        <div className="event-guest-avatar">
                          {g.user?.profileImageUrl ? <img src={g.user.profileImageUrl} alt="" /> : (g.user?.username?.[0] || '?').toUpperCase()}
                        </div>
                        <div className="event-guest-info">
                          <Link to={`/profile/${g.user?.username}`} className="event-guest-name">{g.user?.username}</Link>
                          <span className="event-guest-sub">{g.rsvp === 'attending' ? 'Going' : g.rsvp === 'interested' ? 'Interested' : 'Invited'}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

            </div>
          </>
      </div>
      </div>

      {/* Car pick modal — shown after Going RSVP */}
      {showCarPick && (
        <CarPickModal
          eventId={event.id}
          userId={currentUserId}
          onAdded={(eventVehicle, uv) => {
            setCarsAttending(prev => [...prev, { ...eventVehicle, userVehicle: uv }]);
            setCarsLoaded(true);
            setShowCarPick(false);
            setActiveTab('cars');
            toast.success('Vehicle added to event');
          }}
          onSkip={() => setShowCarPick(false)}
        />
      )}
    </div>
  );
};

// ─── Events Map ───────────────────────────────────────────────────────────────
const makeEventPin = (color, event) => {
  let label = '';
  if (event?.eventDate) {
    const d = new Date(event.eventDate);
    const dateStr = d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
    const timeStr = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    label = `${dateStr} · ${timeStr}`;
  }
  const PIN_W = 22;
  const PIN_H = 30;
  // The white centre dot vanishes on the light cream 'Cars & Coffee' fill, so
  // that pin gets an espresso dot instead.
  const dot = color === categoryColor['Cars & Coffee'] ? '#4a3524' : '#fff';
  // Teardrop pin SVG: filled with the category color, white outline, drop shadow
  const pinSvg = `<svg width="${PIN_W}" height="${PIN_H}" viewBox="0 0 22 30" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.6));">
    <path d="M11 0C4.9 0 0 4.9 0 11c0 7.4 9.5 17.4 10.4 18.3.3.3.9.3 1.2 0C12.5 28.4 22 18.4 22 11 22 4.9 17.1 0 11 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
    <circle cx="11" cy="11" r="3.5" fill="${dot}"/>
  </svg>`;
  const labelHtml = label
    ? `<div style="background:rgba(12,15,26,0.92);border:1px solid rgba(255,255,255,0.15);color:#e8eaf0;font-size:0.6rem;font-weight:700;padding:2px 7px;white-space:nowrap;letter-spacing:0.04em;border-radius:3px;margin-bottom:2px;">${label}</div>`
    : '';
  const html = `<div style="display:flex;flex-direction:column;align-items:center;">${labelHtml}${pinSvg}</div>`;
  const totalH = (label ? 18 : 0) + PIN_H;
  return L.divIcon({
    className: '',
    html,
    iconSize: [Math.max(PIN_W, 130), totalH],
    iconAnchor: [Math.max(PIN_W, 130) / 2, totalH], // bottom-center (tip of pin)
    popupAnchor: [0, -totalH],
  });
};

const MapBoundsWatcher = ({ onBoundsChange }) => {
  useMapEvents({
    moveend: (e) => onBoundsChange(e.target.getBounds()),
    zoomend: (e) => onBoundsChange(e.target.getBounds()),
  });
  return null;
};

const EventsMap = ({ events, onEventClick, onRsvp }) => {
  const [bounds, setBounds] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [selected, setSelected] = useState(null);
  const panelRef = useRef(null);

  const eventsWithCoords = events.filter(e => e.latitude && e.longitude);

  const visibleEvents = bounds
    ? eventsWithCoords.filter(e =>
        bounds.contains([parseFloat(e.latitude), parseFloat(e.longitude)])
      )
    : eventsWithCoords;

  // Scroll highlighted card into view
  useEffect(() => {
    if (!hoveredId || !panelRef.current) return;
    const el = panelRef.current.querySelector(`[data-id="${hoveredId}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [hoveredId]);

  const [userLocation, setUserLocation] = useState(null);
  const [locationReady, setLocationReady] = useState(false);

  useEffect(() => {
    requestCoords()
      .then(({ lat, lng }) => setUserLocation([lat, lng]))
      .catch(() => {})
      .finally(() => setLocationReady(true));
  }, []);

  // 50km radius ≈ zoom 10. Fall back to Australia overview if no location.
  const centre = userLocation || [-25.2744, 133.7751];
  const zoom = userLocation ? 12 : 4;

  if (!locationReady) return (
    <div className="events-map-layout">
      <div className="events-map-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-spinner" />
      </div>
    </div>
  );

  return (
    <div className="events-map-layout">
      {/* Map */}
      <div className="events-map-container">
        <MapContainer
          center={centre}
          zoom={zoom}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
            tileSize={512}
            zoomOffset={-1}
            attribution="&copy; Mapbox"
          />
          <MapBoundsWatcher onBoundsChange={setBounds} />
          {eventsWithCoords.map(event => {
            const color = categoryColor[event.category] || categoryColor['Other'];
            return (
              <Marker
                key={event.id}
                position={[parseFloat(event.latitude), parseFloat(event.longitude)]}
                icon={makeEventPin(color, event)}
                eventHandlers={{
                  click: () => setSelected(event),
                  mouseover: () => setHoveredId(event.id),
                  mouseout: () => setHoveredId(null),
                }}
              />
            );
          })}
        </MapContainer>

        {selected && (() => {
          const color = categoryColor[selected.category] || categoryColor['Other'];
          return (
            <div className="places-map-callout places-map-callout--media" onClick={() => onEventClick(selected)} role="button">
              {selected.bannerImageUrl
                ? <img src={eventBannerThumb(selected)} alt="" className="places-map-callout-img" loading="lazy" decoding="async" />
                : <div className="places-map-callout-colorbar" style={{ background: color }} />
              }
              <div className="places-map-callout-body">
                <div className="places-map-callout-head">
                  <span className="places-map-callout-chip" style={{ color }}>
                    {selected.category || 'Event'}
                    {selected.myStatus === 'attending' ? ' · Going' : ''}
                  </span>
                  <button
                    className="places-map-callout-close"
                    onClick={(e) => { e.stopPropagation(); setSelected(null); }}
                    aria-label="Close"
                  >×</button>
                </div>
                <div className="places-map-callout-title">{selected.title}</div>
                <div className="places-map-callout-meta">
                  {selected.eventDate && (
                    <span className="places-map-callout-meta-item">
                      {formatDate(selected.eventDate)} · {formatTime(selected.eventDate)}
                    </span>
                  )}
                  {selected.attendeeCount > 0 && (
                    <span className="places-map-callout-meta-item">
                      {selected.attendeeCount} going
                    </span>
                  )}
                </div>
                <div className="places-map-callout-cta">
                  View details
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M3 6h6m-3-3l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Side panel */}
      <div className="events-map-panel" ref={panelRef}>
        <div className="events-map-panel-header">
          {visibleEvents.length} event{visibleEvents.length !== 1 ? 's' : ''} in view
        </div>

        {visibleEvents.length === 0 ? (
          <div className="events-map-empty">
            Pan the map to find events
          </div>
        ) : (
          visibleEvents.map(event => {
            const color = categoryColor[event.category] || categoryColor['Other'];
            return (
              <div
                key={event.id}
                data-id={event.id}
                className={`events-map-card${hoveredId === event.id ? ' highlighted' : ''}`}
                onClick={() => onEventClick(event)}
                onMouseEnter={() => setHoveredId(event.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="events-map-card-dot" style={{ background: color }} />
                <div className="events-map-card-content">
                  <div className="events-map-card-category" style={{ color }}>{event.category || 'Event'}</div>
                  <div className="events-map-card-title">{event.title}</div>
                  <div className="events-map-card-meta">
                    {event.eventDate && (
                      <span>{formatDate(event.eventDate)} · {formatTime(event.eventDate)}</span>
                    )}
                    {event.address && <span>{event.address}</span>}
                  </div>
                  <div className="events-map-card-rsvp" onClick={e => e.stopPropagation()}>
                    <RsvpButtons myStatus={event.myStatus} onRsvp={(status) => onRsvp(event, status)} compact />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Filter / Sort dropdowns (match Routes + Landmarks design) ───────────────
const checkMark = (
  <svg className="route-sort-check" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3 5-5" stroke="#4a9eff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EventFilterDropdown = ({ activeFilters, toggleFilter, resetFilters, whenFilter, setWhenFilter, searchQuery, setSearchQuery }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const searchRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus the search field as soon as the panel opens so the user can type.
  useEffect(() => { if (open) searchRef.current?.focus(); }, [open]);

  const hasSearch = !!searchQuery.trim();
  const emptyCats = activeFilters.size === 0;
  const noWhen = !whenFilter;
  const empty = emptyCats && noWhen && !hasSearch;
  const activeWhenLabel = whenFilter ? WHEN_OPTIONS.find(w => w.key === whenFilter)?.label : null;
  const soloLabel = (emptyCats && !hasSearch && activeWhenLabel)
    ? activeWhenLabel
    : (noWhen && !hasSearch && activeFilters.size === 1 ? Array.from(activeFilters)[0] : null);
  const totalCount = activeFilters.size + (whenFilter ? 1 : 0) + (hasSearch ? 1 : 0);
  const multiCount = totalCount > 1 ? totalCount : 0;

  const clearAll = () => { resetFilters(); setWhenFilter(null); setSearchQuery(''); };

  return (
    <div className="route-sort-dropdown" ref={ref}>
      <button className="route-sort-trigger" onClick={() => setOpen(o => !o)}>
        <svg className="route-sort-trigger-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 4h18M6 12h12M10 20h4"/>
        </svg>
        <span className="route-sort-trigger-label">
          Filter{soloLabel ? <>: <strong>{soloLabel}</strong></> : multiCount ? <> · <strong>{multiCount}</strong></> : ''}
        </span>
        <svg className={`route-sort-chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="route-sort-menu route-filter-menu">
          <div className="route-filter-search">
            <svg className="route-filter-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchRef}
              className="route-filter-search-input"
              type="text"
              placeholder="Search events…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setSearchQuery(''); } }}
            />
            {hasSearch && (
              <button className="route-filter-search-clear" onMouseDown={e => { e.preventDefault(); setSearchQuery(''); searchRef.current?.focus(); }} aria-label="Clear search">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          <div className="route-sort-section-label">
            When
            {!empty && (
              <button className="route-sort-section-clear" onMouseDown={clearAll}>Reset</button>
            )}
          </div>
          <button
            className={`route-sort-item${noWhen ? ' active' : ''}`}
            onMouseDown={() => { if (whenFilter) setWhenFilter(null); }}
          >
            <span className="route-sort-item-label">Any time</span>
            {noWhen && checkMark}
          </button>
          {WHEN_OPTIONS.map(opt => {
            const active = whenFilter === opt.key;
            return (
              <button
                key={opt.key}
                className={`route-sort-item${active ? ' active' : ''}`}
                onMouseDown={() => setWhenFilter(active ? null : opt.key)}
              >
                <span className="route-sort-item-icon" style={{ color: '#8892b0' }}>
                  <CalIcon />
                </span>
                <span className="route-sort-item-label">{opt.label}</span>
                {active && checkMark}
              </button>
            );
          })}

          <div className="route-sort-section-label" style={{ marginTop: 6 }}>
            Categories
          </div>
          <button
            className={`route-sort-item${emptyCats ? ' active' : ''}`}
            onMouseDown={() => { if (!emptyCats) resetFilters(); }}
          >
            <span className="route-sort-item-label">All Categories</span>
            {emptyCats && checkMark}
          </button>
          {EVENT_TYPES.map(type => {
            const active = activeFilters.has(type);
            const color = categoryColor[type];
            return (
              <button
                key={type}
                className={`route-sort-item${active ? ' active' : ''}`}
                onMouseDown={() => toggleFilter(type)}
              >
                <span className="route-sort-item-icon" style={{ color }}>●</span>
                <span className="route-sort-item-label">{type}</span>
                {active && checkMark}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const EventSortDropdown = ({ sortBy, setSortBy }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const active = SORT_OPTIONS.find(o => o.key === sortBy);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="route-sort-dropdown" ref={ref}>
      <button className="route-sort-trigger" onClick={() => setOpen(o => !o)}>
        <span className="route-sort-trigger-icon">{active?.icon}</span>
        <span className="route-sort-trigger-label">Sort: <strong>{active?.label}</strong></span>
        <svg className={`route-sort-chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="route-sort-menu">
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.key}
              className={`route-sort-item${sortBy === opt.key ? ' active' : ''}`}
              onClick={() => { setSortBy(opt.key); setOpen(false); }}
            >
              <span className="route-sort-item-icon">{opt.icon}</span>
              <span className="route-sort-item-label">{opt.label}</span>
              {sortBy === opt.key && checkMark}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Events Page ─────────────────────────────────────────────────────────
const Events = () => {
  const { user } = useSelector(s => s.auth);
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeFilters, setActiveFilters] = useState(new Set()); // empty = show all
  const [searchQuery, setSearchQuery] = useState('');
  const [whenFilter, setWhenFilter] = useState(null); // null | 'today' | 'tomorrow' | 'weekend'
  const [sortBy, setSortBy] = useState('featured');
  const [userCoords, setUserCoords] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  // Try the geolocation cache first; otherwise prompt the browser. This drives
  // the proximity factor in the featured sort so events far from the user get
  // demoted heavily after ~100 km.
  useEffect(() => {
    const cached = getCachedCoords();
    if (cached) {
      setUserCoords({ lat: cached.lat, lon: cached.lng });
      return;
    }
    requestCoords()
      .then(pos => setUserCoords({ lat: pos.lat, lon: pos.lng }))
      .catch(() => {});
  }, []);

  // Picking "Nearest" without coords yet → prompt for location.
  useEffect(() => {
    if (sortBy !== 'nearest' || userCoords) return;
    requestCoords()
      .then(pos => setUserCoords({ lat: pos.lat, lon: pos.lng }))
      .catch(() => {});
  }, [sortBy, userCoords]);

  // Open event from ?event=ID URL param (shared links)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventId = params.get('event');
    if (!eventId) return;
    api.get(`/events/${eventId}`)
      .then(({ data }) => setSelectedEvent({ ...data.event, myStatus: data.myStatus }))
      .catch(() => {});
  }, [location.search]);

  // Shared-link arrivals on iOS/Android prefer the native app. The global gate
  // in App.jsx attempts the native open before the auth gate; here we just
  // surface the manual "Open in app" banner as a fallback for authed viewers
  // who landed on the web event (in-app browsers, pasted URLs, search results).
  const [showAppBanner, setShowAppBanner] = useState(false);
  useEffect(() => {
    setShowAppBanner(isMobileSharedLink(location.pathname, location.search));
  }, [location.pathname, location.search]);

  const handleOpenInApp = () => openInApp(location.pathname, location.search);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.get('/events', { params: { upcoming: 'true', limit: 500 } });
      setEvents(data.events || []);
    } catch {
      // Distinguish a failed request from a genuinely empty list — rendering the
      // empty state here tells the user there are no events when we simply don't know.
      setLoadError(true);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleFilter = (type) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };
  const resetFilters = () => setActiveFilters(new Set());

  const handleRsvp = async (event, status) => {
    try {
      if (event.myStatus === status) {
        // Toggle off
        await api.delete(`/events/${event.id}/attend`);
        setEvents(prev => prev.map(e => e.id === event.id ? {
          ...e, myStatus: null,
          attendeeCount: status === 'attending' ? Math.max(0, (e.attendeeCount || 1) - 1) : e.attendeeCount,
          interestedCount: status === 'interested' ? Math.max(0, (e.interestedCount || 1) - 1) : e.interestedCount,
        } : e));
      } else {
        const prev = event.myStatus;
        await api.post(`/events/${event.id}/attend`, { status });
        setEvents(evs => evs.map(e => e.id === event.id ? {
          ...e, myStatus: status,
          attendeeCount: (e.attendeeCount || 0)
            + (status === 'attending' ? 1 : 0)
            - (prev === 'attending' ? 1 : 0),
          interestedCount: (e.interestedCount || 0)
            + (status === 'interested' ? 1 : 0)
            - (prev === 'interested' ? 1 : 0),
        } : e));
      }
    } catch { toast.error('Failed to update RSVP'); }
  };

  const handleCreate = (newEvent) => {
    setEvents(prev => [{ ...newEvent, myStatus: null }, ...prev]);
  };

  const handleUpdate = (updated) => {
    setEvents(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
    setSelectedEvent(prev => ({ ...prev, ...updated }));
  };

  const searchTerm = searchQuery.trim().toLowerCase();
  const displayed = sortEvents(
    events.filter(e => {
      if (activeFilters.size > 0) {
        // An event matches a category filter if ANY of its tags is selected.
        const tags = eventTags(e);
        const list = tags.length ? tags : ['Other'];
        if (!list.some(t => activeFilters.has(t))) return false;
      }
      if (!matchesWhen(whenFilter, e.eventDate)) return false;
      if (searchTerm) {
        const haystack = `${e.title || ''} ${e.description || ''} ${e.address || ''} ${e.suburb || ''} ${e.organizer?.username || ''} ${eventTags(e).join(' ')}`.toLowerCase();
        if (!haystack.includes(searchTerm)) return false;
      }
      return true;
    }),
    sortBy,
    userCoords,
  );

  return (
    <div className="container page">
      {showAppBanner && (
        <div className="open-in-app-banner">
          <div className="open-in-app-banner-text">
            <strong>MyCarCollection</strong>
            <span>Open this event in the app</span>
          </div>
          <button className="open-in-app-banner-btn" onClick={handleOpenInApp}>Open</button>
          <button
            className="open-in-app-banner-dismiss"
            onClick={() => setShowAppBanner(false)}
            aria-label="Dismiss"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}
      <div className="events-header">
        <div className="events-header-left" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingTop: 0 }}>
          <EventFilterDropdown
            activeFilters={activeFilters}
            toggleFilter={toggleFilter}
            resetFilters={resetFilters}
            whenFilter={whenFilter}
            setWhenFilter={setWhenFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
          {viewMode === 'list' && <EventSortDropdown sortBy={sortBy} setSortBy={setSortBy} />}
        </div>
        <div className="events-header-right">
          <div className="events-view-toggle">
            <button
              className={`events-view-btn${viewMode === 'list' ? ' active' : ''}`}
              onClick={() => setViewMode('list')}
            >List</button>
            <button
              className={`events-view-btn${viewMode === 'map' ? ' active' : ''}`}
              onClick={() => setViewMode('map')}
            >Map</button>
          </div>
          <button className="btn-primary events-create-btn" onClick={() => setShowCreate(true)}>+ Create Event</button>
        </div>
      </div>

      {/* Content — list or map */}
      {viewMode === 'map' ? (
        <EventsMap
          events={displayed}
          onEventClick={setSelectedEvent}
          onRsvp={handleRsvp}
        />
      ) : loading ? (
        <div className="loading-page"><div className="loading-spinner" /></div>
      ) : loadError ? (
        <LoadError
          title="Couldn’t load events"
          message="We couldn’t reach the server. Check your connection and try again."
          onRetry={load}
        />
      ) : displayed.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 48 }}>
          <div className="empty-icon">—</div>
          <h3>No upcoming events</h3>
          <p style={{ color: '#5a657a', fontSize: '0.875rem' }}>Be the first to create one.</p>
        </div>
      ) : (
        <div className="events-list">
          {displayed.map(event => (
            <EventCard key={event.id} event={event} onRsvp={handleRsvp} onClick={() => setSelectedEvent(event)} currentUserId={user?.id} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateEventModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          currentUserId={user?.id}
          onClose={() => setSelectedEvent(null)}
          onRsvp={(event, status) => {
            handleRsvp(event, status);
          }}
          onUpdate={handleUpdate}
          onDelete={(id) => { setEvents(prev => prev.filter(e => e.id !== id)); }}
        />
      )}
    </div>
  );
};

export default Events;
